// City Skyline — cyberpunk / Blade Runner cityscape driven by price data

// ── Layout constants ──────────────────────────────────────────────────────────
const CS_GROUND_Y = 1.0;
const CS_TOP_MIN = 0.52; // at max price (tallest)
const CS_TOP_MAX = 0.84; // at min price (shortest)
const CS_COL_W_MIN = 18;
const CS_COL_W_MAX = 52;
const CS_MOD_H_MIN = 14;
const CS_MOD_H_MAX = 44;
const CS_ROOF_H_MIN = 6;
const CS_ROOF_H_MAX = 22;
const CS_OVERHANG = 8;
const CS_PRICE_WIRE_CLEARANCE_MIN = 16;
const CS_PRICE_WIRE_CLEARANCE_MAX = 34;
const CS_BLEND = 0.65;
const CS_PASSES = 3;

// 0=flat  1=shed-R  2=shed-L  3=gable  4=dome  5=spire
const CS_ROOF_POOL = [1, 1, 2, 2, 3, 3, 4, 4, 5, 0, 0, 0];

// Building fills — near-black nighttime materials so neon colors pop
const CS_BLDG = [
    [16, 17, 22], // steel/glass — very dark blue-grey
    [12, 13, 19], // glass tower — near-black blue-grey
    [20, 17, 13], // concrete — very dark warm
    [17, 14, 10], // aged brick — very dark warm
    [28, 27, 24], // pale concrete — darkest medium
    [22, 20, 17], // weathered concrete — dark warm
    [9, 9, 12], // dark stone/shadow — near black
    [15, 14, 13], // dark masonry — neutral dark
];

// Neon accent colors [r, g, b]
const CS_NEON = [
    [0, 220, 255],
    [255, 0, 200],
    [255, 170, 0],
    [80, 255, 50],
    [0, 180, 255],
    [255, 80, 100],
];

// Structural line color (light on dark)
const CS_ST = [150, 140, 195];

// Alien-script glyph set — each fn draws one character into the current ctx state
const _ALIEN_GLYPHS = [
    (c, x, y, w, h) => {
        c.beginPath();
        c.moveTo(x, y + h / 2);
        c.lineTo(x + w, y + h / 2);
        c.stroke();
    },
    (c, x, y, w, h) => {
        c.beginPath();
        c.moveTo(x + w / 2, y);
        c.lineTo(x + w / 2, y + h);
        c.stroke();
    },
    (c, x, y, w, h) => {
        c.beginPath();
        c.moveTo(x, y + h / 2);
        c.lineTo(x + w, y + h / 2);
        c.moveTo(x + w / 2, y);
        c.lineTo(x + w / 2, y + h);
        c.stroke();
    },
    (c, x, y, w, h) => {
        c.beginPath();
        c.moveTo(x, y);
        c.lineTo(x + w / 2, y + h);
        c.lineTo(x + w, y);
        c.stroke();
    },
    (c, x, y, w, h) => {
        c.beginPath();
        c.moveTo(x, y + h);
        c.lineTo(x + w / 2, y);
        c.lineTo(x + w, y + h);
        c.stroke();
    },
    (c, x, y, w, h) => {
        c.beginPath();
        c.rect(x, y + h * 0.1, w, h * 0.8);
        c.stroke();
    },
    (c, x, y, w, h) => {
        c.beginPath();
        c.arc(x + w / 2, y + h / 2, Math.min(w, h) * 0.28, 0, Math.PI * 2);
        c.fill();
    },
    (c, x, y, w, h) => {
        c.beginPath();
        c.moveTo(x, y);
        c.lineTo(x + w, y);
        c.lineTo(x, y + h);
        c.lineTo(x + w, y + h);
        c.stroke();
    },
    (c, x, y, w, h) => {
        c.beginPath();
        c.moveTo(x + w, y);
        c.lineTo(x, y);
        c.lineTo(x + w, y + h);
        c.lineTo(x, y + h);
        c.stroke();
    },
    (c, x, y, w, h) => {
        c.beginPath();
        c.moveTo(x, y);
        c.lineTo(x + w, y);
        c.moveTo(x + w / 2, y);
        c.lineTo(x + w / 2, y + h);
        c.stroke();
    },
    (c, x, y, w, h) => {
        c.beginPath();
        c.moveTo(x, y + h);
        c.lineTo(x + w, y + h);
        c.moveTo(x + w / 2, y + h);
        c.lineTo(x + w / 2, y);
        c.stroke();
    },
    (c, x, y, w, h) => {
        c.beginPath();
        c.moveTo(x, y);
        c.lineTo(x, y + h);
        c.lineTo(x + w, y + h);
        c.stroke();
    },
    (c, x, y, w, h) => {
        c.beginPath();
        c.moveTo(x + w, y);
        c.lineTo(x + w, y + h);
        c.lineTo(x, y + h);
        c.stroke();
    },
    (c, x, y, w, h) => {
        c.beginPath();
        c.moveTo(x, y + h);
        c.lineTo(x, y);
        c.lineTo(x + w, y + h);
        c.lineTo(x + w, y);
        c.stroke();
    },
    (c, x, y, w, h) => {
        c.beginPath();
        c.moveTo(x + w / 2, y);
        c.lineTo(x + w, y + h / 2);
        c.lineTo(x + w / 2, y + h);
        c.lineTo(x, y + h / 2);
        c.closePath();
        c.stroke();
    },
    (c, x, y, w, h) => {
        c.beginPath();
        c.moveTo(x, y + h * 0.3);
        c.lineTo(x + w, y + h * 0.3);
        c.moveTo(x, y + h * 0.7);
        c.lineTo(x + w, y + h * 0.7);
        c.stroke();
    },
];

// Billboard text pool — cyberpunk words and kanji for large signs
const _SIGN_WORDS = [
    'VIP',
    '24H',
    'BAR',
    '未来',
    '都市',
    '電子',
    '企業',
    '警告',
    'ホテル',
    'バー',
    '夜',
    '危険',
    '禁止',
];

const _KANJI_ONLY = [
    '神',
    '龍',
    '火',
    '光',
    '影',
    '夢',
    '天',
    '命',
    '道',
    '力',
    '剣',
    '鬼',
    '風',
    '水',
    '金',
    '幻',
    '魂',
    '死',
    '生',
    '空',
    '嵐',
    '闇',
    '雷',
    '虎',
    '蛇',
    '鳥',
    '星',
    '月',
    '刃',
    '誠',
];

// Curated ticker pool for Blade Runner-style advertisements
const _TICKER_ADS = [
    { sym: 'AAPL', name: 'APPLE INC', type: 'stock' },
    { sym: 'MSFT', name: 'MICROSOFT', type: 'stock' },
    { sym: 'NVDA', name: 'NVIDIA CORP', type: 'stock' },
    { sym: 'AMZN', name: 'AMAZON', type: 'stock' },
    { sym: 'GOOGL', name: 'ALPHABET', type: 'stock' },
    { sym: 'META', name: 'META', type: 'stock' },
    { sym: 'TSLA', name: 'TESLA', type: 'stock' },
    { sym: 'JPM', name: 'JPMORGAN', type: 'stock' },
    { sym: 'NFLX', name: 'NETFLIX', type: 'stock' },
    { sym: 'AMD', name: 'AMD', type: 'stock' },
    { sym: 'COIN', name: 'COINBASE', type: 'stock' },
    { sym: 'INTC', name: 'INTEL', type: 'stock' },
    { sym: 'ORCL', name: 'ORACLE', type: 'stock' },
    { sym: 'ADBE', name: 'ADOBE', type: 'stock' },
    { sym: 'IBM', name: 'IBM', type: 'stock' },
    { sym: 'BTC', name: 'BITCOIN', type: 'crypto' },
    { sym: 'ETH', name: 'ETHEREUM', type: 'crypto' },
    { sym: 'SOL', name: 'SOLANA', type: 'crypto' },
    { sym: 'XRP', name: 'RIPPLE', type: 'crypto' },
    { sym: 'BNB', name: 'BINANCE', type: 'crypto' },
    { sym: 'ADA', name: 'CARDANO', type: 'crypto' },
    { sym: 'DOGE', name: 'DOGECOIN', type: 'crypto' },
    { sym: 'AVAX', name: 'AVALANCHE', type: 'crypto' },
    { sym: 'LINK', name: 'CHAINLINK', type: 'crypto' },
    { sym: 'DOT', name: 'POLKADOT', type: 'crypto' },
    { sym: 'MATIC', name: 'POLYGON', type: 'crypto' },
    { sym: 'UNI', name: 'UNISWAP', type: 'crypto' },
    { sym: 'EUR/USD', name: 'EURO', type: 'forex' },
    { sym: 'GBP/USD', name: 'STERLING', type: 'forex' },
    { sym: 'USD/JPY', name: 'YEN', type: 'forex' },
    { sym: 'XAU/USD', name: 'GOLD', type: 'forex' },
];

const _JP_EXCHANGE = [
    'マーケット',
    'トレード証券',
    '先物取引所',
    '暗号資産',
    'デジタル市場',
    '株式取引',
];

// Explicit webpack requires — only these 31 logos are bundled (no dynamic glob)
const _TICKER_LOGO_URLS = {};
const _resolveLogo = (mod) => (typeof mod === 'string' ? mod : (mod?.default ?? null));
try { _TICKER_LOGO_URLS['AAPL']    = _resolveLogo(require('../../assets/images/logos/stocks/AAPL.png'));    } catch(e) { console.warn('[logo] require AAPL failed:', e); }
try { _TICKER_LOGO_URLS['MSFT']    = _resolveLogo(require('../../assets/images/logos/stocks/MSFT.png'));    } catch(e) { console.warn('[logo] require MSFT failed:', e); }
try { _TICKER_LOGO_URLS['NVDA']    = _resolveLogo(require('../../assets/images/logos/stocks/NVDA.png'));    } catch(e) { console.warn('[logo] require NVDA failed:', e); }
try { _TICKER_LOGO_URLS['AMZN']    = _resolveLogo(require('../../assets/images/logos/stocks/AMZN.png'));    } catch(e) { console.warn('[logo] require AMZN failed:', e); }
try { _TICKER_LOGO_URLS['GOOGL']   = _resolveLogo(require('../../assets/images/logos/stocks/GOOGL.png'));   } catch(e) { console.warn('[logo] require GOOGL failed:', e); }
try { _TICKER_LOGO_URLS['META']    = _resolveLogo(require('../../assets/images/logos/stocks/META.png'));    } catch(e) { console.warn('[logo] require META failed:', e); }
try { _TICKER_LOGO_URLS['TSLA']    = _resolveLogo(require('../../assets/images/logos/stocks/TSLA.png'));    } catch(e) { console.warn('[logo] require TSLA failed:', e); }
try { _TICKER_LOGO_URLS['JPM']     = _resolveLogo(require('../../assets/images/logos/stocks/JPM.png'));     } catch(e) { console.warn('[logo] require JPM failed:', e); }
try { _TICKER_LOGO_URLS['NFLX']    = _resolveLogo(require('../../assets/images/logos/stocks/NFLX.png'));    } catch(e) { console.warn('[logo] require NFLX failed:', e); }
try { _TICKER_LOGO_URLS['AMD']     = _resolveLogo(require('../../assets/images/logos/stocks/AMD.png'));     } catch(e) { console.warn('[logo] require AMD failed:', e); }
try { _TICKER_LOGO_URLS['COIN']    = _resolveLogo(require('../../assets/images/logos/stocks/COIN.png'));    } catch(e) { console.warn('[logo] require COIN failed:', e); }
try { _TICKER_LOGO_URLS['INTC']    = _resolveLogo(require('../../assets/images/logos/stocks/INTC.png'));    } catch(e) { console.warn('[logo] require INTC failed:', e); }
try { _TICKER_LOGO_URLS['ORCL']    = _resolveLogo(require('../../assets/images/logos/stocks/ORCL.png'));    } catch(e) { console.warn('[logo] require ORCL failed:', e); }
try { _TICKER_LOGO_URLS['ADBE']    = _resolveLogo(require('../../assets/images/logos/stocks/ADBE.png'));    } catch(e) { console.warn('[logo] require ADBE failed:', e); }
try { _TICKER_LOGO_URLS['IBM']     = _resolveLogo(require('../../assets/images/logos/stocks/IBM.png'));     } catch(e) { console.warn('[logo] require IBM failed:', e); }
try { _TICKER_LOGO_URLS['BTC']     = _resolveLogo(require('../../assets/images/logos/crypto/btc.svg'));     } catch(e) { console.warn('[logo] require BTC failed:', e); }
try { _TICKER_LOGO_URLS['ETH']     = _resolveLogo(require('../../assets/images/logos/crypto/eth.svg'));     } catch(e) { console.warn('[logo] require ETH failed:', e); }
try { _TICKER_LOGO_URLS['SOL']     = _resolveLogo(require('../../assets/images/logos/crypto/sol.svg'));     } catch(e) { console.warn('[logo] require SOL failed:', e); }
try { _TICKER_LOGO_URLS['XRP']     = _resolveLogo(require('../../assets/images/logos/crypto/xrp.svg'));     } catch(e) { console.warn('[logo] require XRP failed:', e); }
try { _TICKER_LOGO_URLS['BNB']     = _resolveLogo(require('../../assets/images/logos/crypto/bnb.svg'));     } catch(e) { console.warn('[logo] require BNB failed:', e); }
try { _TICKER_LOGO_URLS['ADA']     = _resolveLogo(require('../../assets/images/logos/crypto/ada.svg'));     } catch(e) { console.warn('[logo] require ADA failed:', e); }
try { _TICKER_LOGO_URLS['DOGE']    = _resolveLogo(require('../../assets/images/logos/crypto/doge.svg'));    } catch(e) { console.warn('[logo] require DOGE failed:', e); }
try { _TICKER_LOGO_URLS['AVAX']    = _resolveLogo(require('../../assets/images/logos/crypto/avax.svg'));    } catch(e) { console.warn('[logo] require AVAX failed:', e); }
try { _TICKER_LOGO_URLS['LINK']    = _resolveLogo(require('../../assets/images/logos/crypto/link.svg'));    } catch(e) { console.warn('[logo] require LINK failed:', e); }
try { _TICKER_LOGO_URLS['DOT']     = _resolveLogo(require('../../assets/images/logos/crypto/dot.svg'));     } catch(e) { console.warn('[logo] require DOT failed:', e); }
try { _TICKER_LOGO_URLS['MATIC']   = _resolveLogo(require('../../assets/images/logos/crypto/matic.svg'));   } catch(e) { console.warn('[logo] require MATIC failed:', e); }
try { _TICKER_LOGO_URLS['UNI']     = _resolveLogo(require('../../assets/images/logos/crypto/uni.svg'));     } catch(e) { console.warn('[logo] require UNI failed:', e); }
try { _TICKER_LOGO_URLS['EUR/USD'] = _resolveLogo(require('../../assets/images/logos/forex/EUR.png'));      } catch(e) { console.warn('[logo] require EUR/USD failed:', e); }
try { _TICKER_LOGO_URLS['GBP/USD'] = _resolveLogo(require('../../assets/images/logos/forex/GBP.png'));      } catch(e) { console.warn('[logo] require GBP/USD failed:', e); }
try { _TICKER_LOGO_URLS['USD/JPY'] = _resolveLogo(require('../../assets/images/logos/forex/USD.png'));      } catch(e) { console.warn('[logo] require USD/JPY failed:', e); }
try { _TICKER_LOGO_URLS['XAU/USD'] = _resolveLogo(require('../../assets/images/logos/forex/XAU.png'));      } catch(e) { console.warn('[logo] require XAU/USD failed:', e); }
// Pre-load all logo images at module level so they're ready before draw runs.
// p5 v2 has no preload lifecycle; HTMLImageElement works directly with ctx.drawImage.
const _LOGO_ELEMENTS = {};
Object.entries(_TICKER_LOGO_URLS).forEach(([sym, url]) => {
    if (!url) return;
    const img = new Image();
    img.onload = () => { _LOGO_ELEMENTS[sym] = img; };
    img.onerror = () => console.warn('[logo] img load failed', sym);
    img.src = url;
});


const _csSmooth = (prices, passes, blend) => {
    const k = [1, 2, 3, 2, 1],
        kS = 9,
        kC = 2;
    let arr = [...prices];
    for (let pass = 0; pass < passes; pass++) {
        const prev = [...arr];
        arr = prev.map((v, i) => {
            if (i === 0 || i === prev.length - 1) return v;
            const sm =
                k.reduce((s, w, ki) => {
                    const pi = Math.max(
                        0,
                        Math.min(prev.length - 1, i + ki - kC)
                    );
                    return s + prev[pi] * w;
                }, 0) / kS;
            return v * (1 - blend) + sm * blend;
        });
    }
    return arr;
};

const _csJit = (seed, amt) => Math.sin(seed * 137.508 + 0.5) * amt;

// Traces the silhouette path of a background building shape.
// Caller follows with fill(), clip(), or stroke() as needed.
const _traceBgShape = (ctx, x, topY, w, h, kind, seed) => {
    ctx.beginPath();
    if (kind === 'setback') {
        // Wide base with a narrower tier set back at the top.
        const sh = h * (0.35 + Math.abs(Math.sin(seed * 41)) * 0.28);
        const tw = Math.max(Math.min(w * (0.44 + Math.abs(Math.sin(seed * 73)) * 0.36), w - 8), 12);
        const tx = x + Math.max(0, Math.min(w - tw, (w - tw) * (0.1 + Math.abs(Math.sin(seed * 97)) * 0.8)));
        ctx.moveTo(x,       topY + h);
        ctx.lineTo(x + w,   topY + h);
        ctx.lineTo(x + w,   topY + sh);
        ctx.lineTo(tx + tw, topY + sh);
        ctx.lineTo(tx + tw, topY);
        ctx.lineTo(tx,      topY);
        ctx.lineTo(tx,      topY + sh);
        ctx.lineTo(x,       topY + sh);
        ctx.closePath();
    } else if (kind === 'notched') {
        // Rectangle with one upper corner cut away.
        const nw = Math.max(Math.min(w * (0.2 + Math.abs(Math.sin(seed * 53)) * 0.22), w - 10), 8);
        const nh = h * (0.18 + Math.abs(Math.sin(seed * 61)) * 0.32);
        const right = Math.sin(seed * 11) > 0;
        ctx.moveTo(x,     topY + h);
        ctx.lineTo(x + w, topY + h);
        if (right) {
            ctx.lineTo(x + w,      topY + nh);
            ctx.lineTo(x + w - nw, topY + nh);
            ctx.lineTo(x + w - nw, topY);
            ctx.lineTo(x,          topY);
        } else {
            ctx.lineTo(x + w,  topY);
            ctx.lineTo(x + nw, topY);
            ctx.lineTo(x + nw, topY + nh);
            ctx.lineTo(x,      topY + nh);
        }
        ctx.closePath();
    } else {
        ctx.rect(x, topY, w, h);
    }
};

// Returns the x, width bounds of a building's uppermost roof edge for gable/spire drawing.
const _bgRoofBounds = (x, topY, w, h, kind, seed) => {
    if (kind === 'setback') {
        const sh = h * (0.35 + Math.abs(Math.sin(seed * 41)) * 0.28);
        const tw = Math.max(Math.min(w * (0.44 + Math.abs(Math.sin(seed * 73)) * 0.36), w - 8), 12);
        const tx = x + Math.max(0, Math.min(w - tw, (w - tw) * (0.1 + Math.abs(Math.sin(seed * 97)) * 0.8)));
        return { rx: tx, rw: tw };
    }
    if (kind === 'notched') {
        const nw = Math.max(Math.min(w * (0.2 + Math.abs(Math.sin(seed * 53)) * 0.22), w - 10), 8);
        const right = Math.sin(seed * 11) > 0;
        return right ? { rx: x, rw: w - nw } : { rx: x + nw, rw: w - nw };
    }
    return { rx: x, rw: w };
};

export const citySkyline = (p, chartData) => {
    let buildings = [];
    let scaffolds = [];
    let wires = [];
    let stars = [];
    let bgDeep = [];
    let bgFar = [];
    let bgMid = [];
    let celestial = null;
    let groundGlows = [];
    let skywalks = [];
    let hoverCars = [];
    let ridgePoints = [];

    let skyColors = null; // computed in setup, used in draw
    let assetTickerEntry = null; // the asset being charted, forced onto one hero building
    let weather = null; // null | { kind: 'rain'|'fog', ... }
    let priceWires    = []; // grouped rooftop anchors — traces price silhouette
    let priceWireBridges = []; // price-sampled spans across street gaps
    let priceWireNeon = 0;  // neon index for the price wire chain

    p.setup = () => {
        p.createCanvas(p.windowWidth, p.windowHeight);
        const W = p.width,
            H = p.height;
        const groundY = H * CS_GROUND_Y;

        // ── Celestial body ─────────────────────────────────────────────────
        const isDay = p.random() < 0.25;
        const moonR = isDay ? p.random(22, 40) : p.random(32, 54);

        // Moon craters (only for night)
        const moonCraters = [];
        if (!isDay) {
            const craterTarget = Math.floor(p.random(50, 75));
            for (let i = 0; i < craterTarget; i++) {
                for (let attempt = 0; attempt < 20; attempt++) {
                    const angle = p.random(Math.PI * 2);
                    const dist = p.random(0, moonR * 0.8);
                    const ox = Math.cos(angle) * dist;
                    const oy = Math.sin(angle) * dist;
                    const sizeRoll = p.random();
                    const cr =
                        moonR *
                        (sizeRoll < 0.2
                            ? p.random(0.1, 0.17)
                            : p.random(0.03, 0.1));
                    if (Math.sqrt(ox * ox + oy * oy) + cr > moonR * 0.9)
                        continue;
                    const overlaps = moonCraters.some((c) => {
                        const dx = ox - c.ox,
                            dy = oy - c.oy;
                        return Math.sqrt(dx * dx + dy * dy) < (c.cr + cr) * 1.4;
                    });
                    if (!overlaps) {
                        moonCraters.push({
                            ox,
                            oy,
                            cr,
                            depth: p.random(0.5, 1.0),
                        });
                        break;
                    }
                }
            }
        }

        const moonPhases = ['full', 'gibbous', 'quarter', 'crescent'];
        celestial = {
            x: p.random(W * 0.08, W * 0.92),
            y: p.random(H * 0.04, H * 0.3),
            r: moonR,
            isDay,
            phase: moonPhases[Math.floor(p.random(moonPhases.length))],
            direction: p.random() > 0.5 ? 1 : -1,
            craters: moonCraters,
        };

        // ── Stars ──────────────────────────────────────────────────────────
        const starCount = Math.floor(p.random(300, 500));
        for (let i = 0; i < starCount; i++) {
            stars.push({
                x: p.random(0, W),
                y: p.random(0, groundY * 0.95),
                r: p.random(0.3, 1.4),
                alpha: p.random(0.2, 0.9),
            });
        }

        // ── Sky colour palette (random hue) ───────────────────────────────
        const _hsl = (h, s, l) => {
            h = ((h % 360) + 360) % 360;
            const c = (1 - Math.abs(2 * l - 1)) * s;
            const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
            const m = l - c / 2;
            let r, g, b;
            if (h < 60) {
                r = c;
                g = x;
                b = 0;
            } else if (h < 120) {
                r = x;
                g = c;
                b = 0;
            } else if (h < 180) {
                r = 0;
                g = c;
                b = x;
            } else if (h < 240) {
                r = 0;
                g = x;
                b = c;
            } else if (h < 300) {
                r = x;
                g = 0;
                b = c;
            } else {
                r = c;
                g = 0;
                b = x;
            }
            return [
                Math.round((r + m) * 255),
                Math.round((g + m) * 255),
                Math.round((b + m) * 255),
            ];
        };
        const skyBands = [
            [220, 260],
            [260, 300],
            [300, 330],
            [180, 215],
            [10, 40],
        ];
        const skyBand = skyBands[Math.floor(p.random(skyBands.length))];
        const skyH = p.random(skyBand[0], skyBand[1]);
        const skyS = p.random(0.85, 1.0);
        skyColors = {
            s0: [0, 0, 0],
            s1: _hsl(skyH, skyS, 0.01),
            s2: _hsl(skyH, skyS, 0.035),
            s3: _hsl(skyH, skyS, 0.08),
            hz: _hsl(skyH, skyS, 0.18),
            gl: _hsl(skyH, skyS, 0.35),
            gc: _hsl((skyH + 160) % 360, skyS, 0.35),
            ga: _hsl((skyH + 200) % 360, skyS, 0.3),
        };

        // ── Price → city silhouette ────────────────────────────────────────
        const vals = chartData?.values || [];
        if (!vals.length) {
            p.noLoop();
            return;
        }
        const closes = vals
            .map((v) => parseFloat(v.close))
            .filter(Number.isFinite);
        const smooth = _csSmooth(closes, CS_PASSES, CS_BLEND);
        const minP = Math.min(...smooth);
        const maxP = Math.max(...smooth);
        const priceR = maxP - minP || 1;
        const N = closes.length;

        const cityTopAt = (x) => {
            const frac = Math.max(0, Math.min(1, x / W));
            const fi = frac * (N - 1);
            const i0 = Math.floor(fi);
            const i1 = Math.min(i0 + 1, N - 1);
            const t = fi - i0;
            const ph =
                ((smooth[i0] - minP) / priceR) * (1 - t) +
                ((smooth[i1] - minP) / priceR) * t;
            return H * (CS_TOP_MAX - (CS_TOP_MAX - CS_TOP_MIN) * ph);
        };
        const priceWireClearance = Math.max(
            CS_PRICE_WIRE_CLEARANCE_MIN,
            Math.min(CS_PRICE_WIRE_CLEARANCE_MAX, H * 0.035)
        );
        const buildingTopAt = (x) =>
            Math.min(
                groundY - CS_MOD_H_MIN,
                cityTopAt(x) + priceWireClearance
            );

        // ── Foreground city blocks ────────────────────────────────────────
        const cityBlocks = [];
        const edgeGap = 0;
        const streetGapMin = Math.max(22, W * 0.03);
        const streetGapMax = Math.max(streetGapMin + 8, W * 0.07);
        const minBlockW = Math.max(CS_COL_W_MAX * 1.6, W * 0.11);
        let blockCount = Math.max(2, Math.min(5, Math.floor(W / 360) + 2));
        let streetGaps = [];
        let blockWidths = [];

        while (blockCount >= 2) {
            streetGaps = Array.from({ length: blockCount - 1 }, () =>
                p.random(streetGapMin, streetGapMax)
            );
            const totalStreetGap = streetGaps.reduce(
                (sum, gap) => sum + gap,
                0
            );
            const availableW = W - edgeGap * 2 - totalStreetGap;

            if (availableW >= blockCount * minBlockW || blockCount === 2) {
                const weights = Array.from({ length: blockCount }, () =>
                    p.random(0.85, 1.35)
                );
                const weightTotal = weights.reduce(
                    (sum, weight) => sum + weight,
                    0
                );
                blockWidths = weights.map(
                    (weight) => (availableW * weight) / weightTotal
                );
                break;
            }

            blockCount--;
        }

        let blockX = edgeGap;
        for (let i = 0; i < blockCount; i++) {
            const blockW = Math.max(36, blockWidths[i] ?? W / blockCount);
            cityBlocks.push({ x: blockX, w: Math.min(blockW, W - blockX) });
            blockX += blockW + (streetGaps[i] ?? 0);
        }

        // ── Ridge sample points for draw-time city glow ───────────────────
        const ridgeStep = Math.max(1, Math.floor(W / 12));
        cityBlocks.forEach(({ x, w }) => {
            const blockEnd = Math.min(W, x + w);
            for (let rx = x; rx <= blockEnd; rx += ridgeStep) {
                ridgePoints.push({
                    x: Math.min(rx, blockEnd),
                    y: cityTopAt(Math.min(rx, blockEnd)),
                });
            }
            ridgePoints.push({
                x: blockEnd,
                y: cityTopAt(blockEnd),
            });
        });

        // ── Deep bg city ──────────────────────────────────────────────────
        let bdx = 0;
        while (bdx < W) {
            const bdw = p.random(10, 80);
            const bdh = H * p.random(0.08, 1.0);
            const roofKind =
                p.random() < 0.28
                    ? 'spire'
                    : p.random() < 0.4
                      ? 'gable'
                      : 'flat';
            bgDeep.push({ x: bdx, w: bdw, topY: groundY - bdh, roofKind });
            bdx += bdw;
        }

        // ── Far bg city ───────────────────────────────────────────────────
        let bfx = 0;
        while (bfx < W) {
            const bfw = p.random(16, 110);
            const isTower = p.random() < 0.22;
            const bfh = isTower
                ? H * p.random(0.4, 1.0)
                : H * p.random(0.1, 0.7);
            const roofKind = isTower
                ? 'spire'
                : p.random() < 0.35
                  ? 'gable'
                  : 'flat';
            const shapeKind = isTower ? 'box' : p.random() < 0.44 ? 'box' : p.random() < 0.55 ? 'setback' : 'notched';
            const _bfNeon = Math.floor(p.random(CS_NEON.length));
            bgFar.push({
                x: bfx,
                w: bfw,
                topY: groundY - bfh,
                roofKind,
                shapeKind,
                colIdx: Math.floor(p.random(CS_BLDG.length)),
                neonIdx: _bfNeon,
                seed: Math.floor(bfx * 0.13),
                hasSign: bfw >= 28 && bfh >= H * 0.12 && p.random() < 0.24,
                hasBillboard: bfw >= 38 && bfh >= H * 0.18 && p.random() < 0.15,
                hasTicker: bfw >= 56 && bfh >= H * 0.12 && p.random() < 0.18,
                hasLEDStrips: p.random() < 0.38,
                hasHolo: bfw >= 32 && bfh >= H * 0.22 && p.random() < 0.16,
                hasHelipad:
                    roofKind === 'flat' &&
                    bfw >= 30 &&
                    bfh >= H * 0.12 &&
                    p.random() < 0.09,
                signNeonIdx:
                    p.random() < 0.5
                        ? _bfNeon
                        : Math.floor(p.random(CS_NEON.length)),
                ledNeonIdx:
                    p.random() < 0.5
                        ? _bfNeon
                        : Math.floor(p.random(CS_NEON.length)),
                holoNeonIdx: Math.floor(p.random(CS_NEON.length)),
            });
            bfx += bfw;
        }

        // ── Mid bg city (tracks price) ─────────────────────────────────────
        let bmx = 0;
        while (bmx < W) {
            const bmw = p.random(20, 90);
            const offset = H * p.random(0.02, 0.1);
            const bmTop = cityTopAt(bmx + bmw / 2) + offset;
            if (bmTop < groundY - 8) {
                const _bmNeon = Math.floor(p.random(CS_NEON.length));
                const _bmShape = p.random() < 0.44 ? 'box' : p.random() < 0.55 ? 'setback' : 'notched';
                bgMid.push({
                    x: bmx,
                    w: bmw,
                    topY: bmTop,
                    shapeKind: _bmShape,
                    colIdx: Math.floor(p.random(CS_BLDG.length)),
                    neonIdx: _bmNeon,
                    seed: Math.floor(bmx * 0.17 + bmTop * 0.09),
                    hasSign:
                        bmw >= 24 &&
                        groundY - bmTop >= H * 0.08 &&
                        p.random() < 0.28,
                    hasBillboard:
                        bmw >= 32 &&
                        groundY - bmTop >= H * 0.14 &&
                        p.random() < 0.18,
                    hasTicker:
                        bmw >= 56 &&
                        groundY - bmTop >= H * 0.10 &&
                        p.random() < 0.20,
                    hasLEDStrips: p.random() < 0.45,
                    hasHolo:
                        bmw >= 28 &&
                        groundY - bmTop >= H * 0.16 &&
                        p.random() < 0.22,
                    hasHelipad:
                        bmw >= 26 &&
                        groundY - bmTop >= H * 0.1 &&
                        p.random() < 0.11,
                    signNeonIdx:
                        p.random() < 0.5
                            ? _bmNeon
                            : Math.floor(p.random(CS_NEON.length)),
                    ledNeonIdx:
                        p.random() < 0.5
                            ? _bmNeon
                            : Math.floor(p.random(CS_NEON.length)),
                    holoNeonIdx: Math.floor(p.random(CS_NEON.length)),
                });
            }
            bmx += bmw;
        }

        // ── Asset hero building — always show the charted asset on one building ──
        {
            const assetSym = chartData.meta?.symbol ?? null;
            const found = assetSym ? _TICKER_ADS.find(t => t.sym === assetSym) : null;
            assetTickerEntry = found ?? (assetSym ? { sym: assetSym, name: assetSym, type: 'stock' } : null);
            if (assetTickerEntry) {
                const allBgs = [...bgFar, ...bgMid].filter(b => b.w >= 56 && (groundY - b.topY) >= H * 0.10);
                if (allBgs.length > 0) {
                    const hero = allBgs.reduce((best, b) =>
                        b.w * (groundY - b.topY) > best.w * (groundY - best.topY) ? b : best
                    );
                    hero.hasTicker = true;
                    hero.assetTicker = true;
                }
            }
        }

        // ── Foreground building columns ────────────────────────────────────
        const columnEdges = [];
        cityBlocks.forEach((block, blockIdx) => {
            const blockEnd = Math.min(W, block.x + block.w);
            let cx = block.x;
            while (cx < blockEnd - 8) {
                const remainingW = blockEnd - cx;
                const colW = Math.min(
                    p.random(CS_COL_W_MIN, CS_COL_W_MAX),
                    remainingW
                );
                if (colW < 8) break;
                const centreX = cx + colW / 2;
                const priceY = cityTopAt(centreX);
                const topY = buildingTopAt(centreX);
                columnEdges.push({ x: cx, w: colW, topY, priceY, blockIdx });

                let curY = groundY;
                while (curY > topY + CS_MOD_H_MIN) {
                    const rawH = p.random(CS_MOD_H_MIN, CS_MOD_H_MAX);
                    const modH = Math.min(rawH, curY - topY);
                    const oh = p.random(-CS_OVERHANG, CS_OVERHANG);
                    const isFirstCol = cx === block.x;
                    const isLastCol = colW === remainingW;
                    const bx = isFirstCol
                        ? block.x
                        : Math.max(block.x, cx + oh / 2);
                    const bw = Math.max(
                        8,
                        isLastCol
                            ? blockEnd - bx
                            : Math.min(colW - oh, blockEnd - bx)
                    );
                    const isTop = curY - modH <= topY + CS_MOD_H_MIN * 0.8;
                    let roofType = isTop
                        ? CS_ROOF_POOL[
                              Math.floor(p.random(CS_ROOF_POOL.length))
                          ]
                        : 0;
                    const roofClearance = Math.max(0, topY - priceY - 5);
                    let roofH =
                        roofType !== 0
                            ? Math.min(
                                  p.random(CS_ROOF_H_MIN, CS_ROOF_H_MAX),
                                  roofClearance
                              )
                            : 0;
                    if (roofH < CS_ROOF_H_MIN * 0.65) {
                        roofType = 0;
                        roofH = 0;
                    }
                    const actualBw = roofType === 5 ? Math.min(bw, 28) : bw;

                    // Setback sections stacked on top of the topmost module
                    let sbSections = [];
                    const minSetbackY = priceY + 5;
                    if (
                        isTop &&
                        p.random() < 0.4 &&
                        actualBw >= 14 &&
                        topY - minSetbackY > 8
                    ) {
                        let sbY = curY - modH;
                        let sbW = actualBw;
                        for (let sb = 0; sb < 3; sb++) {
                            sbW = sbW * p.random(0.5, 0.78);
                            if (sbW < 6) break;
                            const sbH = Math.min(
                                p.random(6, 18),
                                sbY - minSetbackY
                            );
                            if (sbH < 5) break;
                            const sbX = bx + (actualBw - sbW) / 2;
                            sbY -= sbH;
                            sbSections.push({
                                x: sbX,
                                y: sbY,
                                w: sbW,
                                h: sbH,
                            });
                        }
                    }

                    buildings.push({
                        x: bx,
                        y: curY - modH,
                        w: actualBw,
                        h: modH,
                        roofType,
                        roofH,
                        seedX: Math.floor(bx * 0.1 + curY * 0.07),
                        colIdx: Math.floor(p.random(CS_BLDG.length)),
                        neonIdx: Math.floor(p.random(CS_NEON.length)),
                        hasSign: isTop && p.random() < 0.22,
                        hasRoofLights: isTop && p.random() < 0.6,
                        sbSections,
                    });
                    curY -= modH;
                }
                cx += colW;
            }
        });

        // ── Skywalks between adjacent columns ─────────────────────────────
        for (let i = 0; i < columnEdges.length - 1; i++) {
            if (p.random() > 0.28) continue;
            const colA = columnEdges[i];
            const colB = columnEdges[i + 1];
            const x1 = colA.x + colA.w;
            const x2 = colB.x;
            if (x2 - x1 > 55) continue; // gap too large
            const bridgeY = Math.max(colA.topY, colB.topY) + p.random(12, 45);
            if (bridgeY > groundY - 16) continue;
            const bridgeH = 3 + p.random(0, 5);
            const neonIdx = Math.floor(p.random(CS_NEON.length));
            const hasGlass = p.random() < 0.55;
            skywalks.push({
                x1,
                x2,
                y: bridgeY,
                h: bridgeH,
                neonIdx,
                hasGlass,
            });
        }

        // ── Hover cars ────────────────────────────────────────────────────
        const carCount = Math.floor(p.random(4, 10));
        for (let i = 0; i < carCount; i++) {
            const carW = p.random(14, 30);
            const carH = p.random(4, 9);
            const dir = p.random() > 0.5 ? 1 : -1;
            const carX = p.random(carW, W - carW);
            const ctop = cityTopAt(carX);
            const maxCarY = ctop - 14;
            const minCarY = H * 0.04;
            if (maxCarY < minCarY + 10) continue;
            const carY = p.random(minCarY, maxCarY);
            const neonIdx = Math.floor(p.random(CS_NEON.length));
            hoverCars.push({
                x: carX,
                y: carY,
                w: carW,
                h: carH,
                dir,
                neonIdx,
            });
        }

        // ── Scaffold panels ────────────────────────────────────────────────
        const scaffoldPositions = [];
        if (columnEdges.length > 0) {
            const lc = columnEdges[0];
            const lTop = cityTopAt(lc.x + lc.w / 2);
            scaffoldPositions.push({
                x: lc.x - p.random(10, 28),
                y: lTop - p.random(5, 18),
                w: p.random(28, 50),
                h: groundY - lTop + p.random(5, 18),
            });
            const rc = columnEdges[columnEdges.length - 1];
            const rTop = cityTopAt(rc.x + rc.w / 2);
            scaffoldPositions.push({
                x: rc.x + rc.w - p.random(5, 12),
                y: rTop - p.random(5, 18),
                w: p.random(28, 50),
                h: groundY - rTop + p.random(5, 18),
            });
        }
        const intCount = Math.floor(p.random(1, 3));
        for (let i = 0; i < intCount; i++) {
            const si = Math.floor(p.random(2, columnEdges.length - 2));
            if (si >= 0 && si < columnEdges.length) {
                const ic = columnEdges[si];
                const iTop = cityTopAt(ic.x + ic.w / 2);
                scaffoldPositions.push({
                    x: ic.x,
                    y: iTop,
                    w: p.random(26, 48),
                    h: groundY - iTop,
                });
            }
        }
        scaffolds = scaffoldPositions;

        // ── Price-data powerlines ─────────────────────────────────────────────
        // Edge anchors plus column-center anchors trace the price silhouette
        // across each block and keep the cable connected to the page edges.
        priceWireNeon = Math.floor(p.random(CS_NEON.length));
        priceWires = cityBlocks
            .map((block, blockIdx) => {
                const blockEnd = Math.min(W, block.x + block.w);
                const points = [
                    {
                        x: block.x,
                        y: cityTopAt(block.x),
                        supportY: buildingTopAt(block.x),
                    },
                    ...columnEdges
                        .filter((col) => col.blockIdx === blockIdx)
                        .map((col) => ({
                            x: col.x + col.w / 2,
                            y: col.priceY,
                            supportY: col.topY,
                        })),
                    {
                        x: blockEnd,
                        y: cityTopAt(blockEnd),
                        supportY: buildingTopAt(blockEnd),
                    },
                ];
                return { points };
            })
            .filter(({ points }) => points.length > 1);

        priceWireBridges = [];
        for (let i = 0; i < priceWires.length - 1; i++) {
            const fromPoints = priceWires[i].points;
            const toPoints = priceWires[i + 1].points;
            const from = fromPoints[fromPoints.length - 1];
            const to = toPoints[0];
            if (!from || !to || to.x <= from.x) continue;

            const span = to.x - from.x;
            const steps = Math.max(3, Math.ceil(span / 42));
            const points = [from];
            for (let step = 1; step < steps; step++) {
                const t = step / steps;
                const x = from.x + span * t;
                const sag =
                    Math.sin(Math.PI * t) * Math.min(span * 0.06, H * 0.035);
                points.push({ x, y: cityTopAt(x) + sag });
            }
            points.push(to);
            priceWireBridges.push({ points });
        }

        // ── Wires anchored to rooftops ─────────────────────────────────────
        const attachPts = [];
        buildings.forEach(({ x, y, w, roofType, roofH }) => {
            if (roofType === 0) return;
            const ov = Math.min(w * 0.07, 5);
            attachPts.push({ x: x - ov, y });
            attachPts.push({ x: x + w + ov, y });
            if (roofType === 3) attachPts.push({ x: x + w / 2, y: y - roofH });
            if (roofType === 5)
                attachPts.push({ x: x + w / 2, y: y - roofH * 2.2 });
        });
        const wireCount = Math.floor(p.random(4, 14));
        for (let i = 0; i < wireCount; i++) {
            if (attachPts.length < 2) break;
            const i1 = Math.floor(p.random(attachPts.length));
            const pt1 = attachPts[i1];
            const pool = attachPts.filter((pt, j) => {
                if (j === i1) return false;
                const dx = Math.abs(pt.x - pt1.x);
                return dx > W * 0.05 && dx < W * 0.55;
            });
            if (!pool.length) continue;
            const pt2 = pool[Math.floor(p.random(pool.length))];
            const dx = Math.abs(pt2.x - pt1.x);
            const isNeon = p.random() < 0.15;
            wires.push({
                x1: pt1.x,
                y1: pt1.y,
                x2: pt2.x,
                y2: pt2.y,
                droop: dx * p.random(0.04, 0.14),
                neonIdx: isNeon ? Math.floor(p.random(CS_NEON.length)) : -1,
            });
        }

        // ── Neon puddle reflections ────────────────────────────────────────
        for (let i = 0; i < 15; i++) {
            groundGlows.push({
                x: p.random(0, W),
                y: p.random(groundY + 5, H - 10),
                r: p.random(30, 140),
                alpha: p.random(0.04, 0.13),
                nc: CS_NEON[Math.floor(p.random(CS_NEON.length))],
            });
        }

        // ── Palette ────────────────────────────────────────────────────────────
        const _toHex = (r, g, b) =>
            '#' +
            [r, g, b]
                .map((v) => Math.round(v).toString(16).padStart(2, '0'))
                .join('');

        // Neon composited over a building background at a given draw alpha.
        // Matches what Canvas 2D actually paints: result = src*a + dst*(1-a).
        const _blendNeon = (nr, ng, nb, alpha, bg) =>
            _toHex(
                Math.round(nr * alpha + bg[0] * (1 - alpha)),
                Math.round(ng * alpha + bg[1] * (1 - alpha)),
                Math.round(nb * alpha + bg[2] * (1 - alpha))
            );

        // Two representative building backgrounds: dark cluster avg, light cluster avg
        const _bgDark = [13, 13, 16]; // avg of darkest new CS_BLDG
        const _bgLight = [23, 21, 18]; // avg of lightest new CS_BLDG

        const { s0, s1, s2, s3, hz, gl, gc, ga } = skyColors;
        p._generatedPalette = [
            // Sky gradient stops (derived from random sky hue)
            _toHex(...s0),
            _toHex(...s1),
            _toHex(...s2),
            _toHex(...s3),
            // Atmospheric haze shades
            _toHex(...hz),
            _toHex(...gl),
            _toHex(...gc),
            _toHex(...ga),
            // Stars / bright highlights
            '#ffffff',
            '#c8d0ff',
            '#eef5ff',
            // Helipad aviation red
            '#ff2d2d',
            '#ff4040',
            // All 8 building material colors
            ...CS_BLDG.map(([r, g, b]) => _toHex(r, g, b)),
            // Ground
            '#0a0420',
            '#060218',
            // bgDeep silhouette
            '#0b0910',
            // All 6 neon colors at full brightness
            ...CS_NEON.map(([r, g, b]) => _toHex(r, g, b)),
            // Blended neons at every draw alpha, over both dark and light building bg.
            // This ensures the quantizer can match semi-transparent window/sign/LED pixels
            // instead of collapsing them to the nearest grey.
            ...CS_NEON.flatMap(([nr, ng, nb]) => [
                _blendNeon(nr, ng, nb, 0.38, _bgDark), // bgFar windows dark bg (old)
                _blendNeon(nr, ng, nb, 0.55, _bgDark), // bgFar windows dark bg (new)
                _blendNeon(nr, ng, nb, 0.55, _bgLight), // bgFar windows light bg (new)
                _blendNeon(nr, ng, nb, 0.52, _bgDark), // bgMid windows dark bg (old)
                _blendNeon(nr, ng, nb, 0.68, _bgDark), // bgMid windows dark bg (new)
                _blendNeon(nr, ng, nb, 0.68, _bgLight), // bgMid windows light bg (new)
                _blendNeon(nr, ng, nb, 0.65, _bgDark), // bgMid LED strips
                _blendNeon(nr, ng, nb, 0.7, _bgDark), // bgFar LED strips
                _blendNeon(nr, ng, nb, 0.78, _bgDark), // fg windows dark bg
                _blendNeon(nr, ng, nb, 0.78, _bgLight), // fg windows light bg
                _blendNeon(nr, ng, nb, 0.82, _bgDark), // signs / hologram
                _blendNeon(nr, ng, nb, 0.9, _bgDark), // billboard text
            ]),
        ];

        // ── Weather ────────────────────────────────────────────────────────────
        if (p.random() < 0.35) {
            const kind = p.random() < 0.5 ? 'rain' : 'fog';
            if (kind === 'fog') {
                const wisps = [];
                const wispCount = Math.floor(p.random(6, 14));
                for (let i = 0; i < wispCount; i++) {
                    wisps.push({
                        x: p.random(0, W),
                        y: p.random(groundY * 0.55, groundY * 1.05),
                        rx: p.random(W * 0.12, W * 0.32),
                        ry: p.random(H * 0.04, H * 0.10),
                        alpha: p.random(0.04, 0.13),
                    });
                }
                weather = { kind: 'fog', wisps, density: p.random(0.12, 0.28) };
            } else {
                const layerDefs = [
                    { count: 60, len: p.random(8, 14),  alpha: 0.10, lw: 0.6 },
                    { count: 90, len: p.random(14, 22), alpha: 0.18, lw: 0.8 },
                    { count: 50, len: p.random(20, 32), alpha: 0.28, lw: 1.0 },
                ];
                const drops = layerDefs.map(({ count, len, alpha, lw }) => {
                    const arr = [];
                    for (let i = 0; i < count; i++) {
                        arr.push({ x: p.random(0, W), y: p.random(0, H), len, alpha, lw });
                    }
                    return arr;
                });
                const splashes = [];
                for (let i = 0; i < 40; i++) {
                    splashes.push({
                        x: p.random(0, W),
                        y: p.random(groundY + 1, H - 2),
                        rx: p.random(3, 9),
                        ry: p.random(1, 2.5),
                        alpha: p.random(0.06, 0.18),
                    });
                }
                weather = { kind: 'rain', drops, splashes, intensity: p.random(0.6, 1.0) };
            }
        }

        p.noLoop();
    };

    // ── Draw ──────────────────────────────────────────────────────────────────
    p.draw = () => {
        const ctx = p.drawingContext;
        const W = p.width,
            H = p.height;
        const groundY = H * CS_GROUND_Y;

        // Sky gradient — near-black top, deep coloured near horizon
        const { s0, s1, s2, s3, hz, gl, gc, ga } = skyColors;
        const skyG = ctx.createLinearGradient(0, 0, 0, groundY);
        skyG.addColorStop(0, `rgb(${s0[0]},${s0[1]},${s0[2]})`);
        skyG.addColorStop(0.28, `rgb(${s1[0]},${s1[1]},${s1[2]})`);
        skyG.addColorStop(0.62, `rgb(${s2[0]},${s2[1]},${s2[2]})`);
        skyG.addColorStop(1, `rgb(${s3[0]},${s3[1]},${s3[2]})`);
        ctx.fillStyle = skyG;
        ctx.fillRect(0, 0, W, groundY);

        // Stars
        ctx.fillStyle = '#ffffff';
        stars.forEach(({ x, y, r, alpha }) => {
            ctx.globalAlpha = alpha;
            ctx.beginPath();
            ctx.arc(x, y, r, 0, Math.PI * 2);
            ctx.fill();
        });
        ctx.globalAlpha = 1;

        // Celestial body
        if (celestial) {
            const { x, y, r, isDay, phase, direction, craters } = celestial;
            if (isDay) {
                const sg = ctx.createRadialGradient(x, y, 0, x, y, r * 5.5);
                sg.addColorStop(0, 'rgba(255,255,180,0.30)');
                sg.addColorStop(0.22, 'rgba(255,200,50,0.14)');
                sg.addColorStop(1, 'rgba(255,100,0,0)');
                ctx.fillStyle = sg;
                ctx.fillRect(x - r * 5.5, y - r * 5.5, r * 11, r * 11);
                ctx.fillStyle = 'rgba(255,255,200,0.95)';
                ctx.beginPath();
                ctx.arc(x, y, r, 0, Math.PI * 2);
                ctx.fill();
            } else {
                _drawMoon(ctx, x, y, r, phase, direction, craters);
            }
        }

        // Atmospheric band — rises up from horizon, widest near the city silhouette
        const hazeLinear = ctx.createLinearGradient(
            0,
            groundY * 0.25,
            0,
            groundY
        );
        hazeLinear.addColorStop(0, 'rgba(0,0,0,0)');
        hazeLinear.addColorStop(0.42, `rgba(${hz[0]},${hz[1]},${hz[2]},0.10)`);
        hazeLinear.addColorStop(0.72, `rgba(${hz[0]},${hz[1]},${hz[2]},0.22)`);
        hazeLinear.addColorStop(1, `rgba(${gl[0]},${gl[1]},${gl[2]},0.40)`);
        ctx.fillStyle = hazeLinear;
        ctx.fillRect(0, 0, W, groundY);

        // Wide radial horizon glow centred at ground level
        const haze1 = ctx.createRadialGradient(
            W / 2,
            groundY,
            0,
            W / 2,
            groundY,
            W * 0.85
        );
        haze1.addColorStop(0, `rgba(${gl[0]},${gl[1]},${gl[2]},0.50)`);
        haze1.addColorStop(0.3, `rgba(${gl[0]},${gl[1]},${gl[2]},0.28)`);
        haze1.addColorStop(0.6, `rgba(${hz[0]},${hz[1]},${hz[2]},0.12)`);
        haze1.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = haze1;
        ctx.fillRect(0, 0, W, H);

        // Warm orange-red accent at street level
        const haze2 = ctx.createRadialGradient(
            W * 0.5,
            groundY,
            0,
            W * 0.5,
            groundY,
            W * 0.38
        );
        haze2.addColorStop(0, 'rgba(200,80,20,0.18)');
        haze2.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = haze2;
        ctx.fillRect(0, 0, W, H);

        // City ambient glow — neon light pollution bleeding up from the building silhouette
        ridgePoints.forEach(({ x: gx, y: gy }, i) => {
            const nc = i % 3 === 0 ? gl : i % 3 === 1 ? gc : ga;
            const glowR = (groundY - gy) * 1.1 + H * 0.08;
            const gg = ctx.createRadialGradient(gx, gy, 0, gx, gy, glowR);
            gg.addColorStop(0, `rgba(${nc[0]},${nc[1]},${nc[2]},0.28)`);
            gg.addColorStop(0.35, `rgba(${nc[0]},${nc[1]},${nc[2]},0.12)`);
            gg.addColorStop(0.7, `rgba(${nc[0]},${nc[1]},${nc[2]},0.04)`);
            gg.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.fillStyle = gg;
            ctx.fillRect(gx - glowR, gy - glowR, glowR * 2, glowR * 2);
        });

        // Deep background
        bgDeep.forEach(({ x, w, topY, roofKind }) => {
            ctx.fillStyle = 'rgb(11,9,16)';
            ctx.fillRect(x, topY, w, groundY - topY);
            if (roofKind !== 'flat') {
                const ph =
                    roofKind === 'gable'
                        ? Math.min((groundY - topY) * 0.18, 14)
                        : Math.min(w * 1.2, 32);
                ctx.strokeStyle = 'rgba(55,35,85,0.22)';
                ctx.lineWidth = 0.5;
                ctx.beginPath();
                ctx.moveTo(x, topY);
                ctx.lineTo(x + w / 2, topY - ph);
                ctx.lineTo(x + w, topY);
                ctx.stroke();
            }
        });

        // Far background
        bgFar.forEach(
            ({
                x,
                w,
                topY,
                colIdx,
                neonIdx,
                seed,
                roofKind,
                shapeKind,
                hasSign,
                hasBillboard,
                hasTicker,
                hasLEDStrips,
                hasHolo,
                hasHelipad,
                signNeonIdx,
                ledNeonIdx,
                holoNeonIdx,
                assetTicker,
            }) => {
                const h = groundY - topY;
                const fc = CS_BLDG[colIdx];
                const nc = CS_NEON[neonIdx];
                ctx.fillStyle = `rgb(${fc[0]},${fc[1]},${fc[2]})`;
                _traceBgShape(ctx, x, topY, w, h, shapeKind, seed);
                ctx.fill();
                // Blue-purple atmospheric tint — far buildings recede into the haze
                ctx.fillStyle = 'rgba(22,10,58,0.22)';
                _traceBgShape(ctx, x, topY, w, h, shapeKind, seed);
                ctx.fill();
                // Per-building neon-tinted outline
                const fBt = 0.2 + Math.abs(Math.sin(seed * 73.1)) * 0.35;
                const fBst = [
                    Math.round(CS_ST[0] * (1 - fBt) + nc[0] * fBt),
                    Math.round(CS_ST[1] * (1 - fBt) + nc[1] * fBt),
                    Math.round(CS_ST[2] * (1 - fBt) + nc[2] * fBt),
                ];
                ctx.save();
                ctx.shadowColor = `rgb(${nc[0]},${nc[1]},${nc[2]})`;
                ctx.shadowBlur = 4;
                ctx.strokeStyle = `rgba(${fBst[0]},${fBst[1]},${fBst[2]},0.42)`;
                ctx.lineWidth = 0.8;
                _traceBgShape(ctx, x, topY, w, h, shapeKind, seed);
                ctx.stroke();
                ctx.restore();
                ctx.save();
                _traceBgShape(ctx, x, topY, w, h, shapeKind, seed);
                ctx.clip();
                const dsx = Math.max(
                    (w - 4) / Math.max(1, Math.floor(w / 8)),
                    7
                );
                for (let dy = topY + 5; dy < topY + h - 4; dy += 9) {
                    for (let dx = x + 3; dx < x + w - 2; dx += dsx) {
                        if (
                            Math.abs(
                                Math.sin(seed * 37.1 + dy * 0.31 + dx * 0.19)
                            ) > 0.46
                        ) {
                            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.55)`;
                            ctx.fillRect(dx, dy, 2, 2);
                        }
                    }
                }
                ctx.restore();
                if (roofKind !== 'flat') {
                    const { rx, rw } = _bgRoofBounds(x, topY, w, h, shapeKind, seed);
                    const ph =
                        roofKind === 'gable'
                            ? Math.min(h * 0.2, 16)
                            : Math.min(rw * 1.3, 40);
                    ctx.strokeStyle = `rgba(${CS_ST[0]},${CS_ST[1]},${CS_ST[2]},0.22)`;
                    ctx.lineWidth = 0.5;
                    ctx.beginPath();
                    ctx.moveTo(rx, topY);
                    ctx.lineTo(rx + rw / 2, topY - ph);
                    ctx.lineTo(rx + rw, topY);
                    ctx.stroke();
                }
                if (hasLEDStrips)
                    _drawLEDStrips(
                        ctx,
                        x,
                        topY,
                        w,
                        h,
                        seed,
                        CS_NEON[ledNeonIdx ?? neonIdx],
                        0.5
                    );
                {
                    const _signs = [
                        hasSign && 'sign',
                        hasBillboard && 'billboard',
                        hasTicker && 'ticker',
                    ].filter(Boolean);
                    const _slotH = _signs.length > 1 ? h / _signs.length : h;
                    const _sNc = CS_NEON[signNeonIdx ?? neonIdx];
                    _signs.forEach((type, si) => {
                        const sBy = topY + si * _slotH;
                        const sBh = _slotH;
                        if (type === 'sign')      _drawBgSign(ctx, x, sBy, w, sBh, seed, _sNc, 0.75);
                        if (type === 'billboard') _drawLargeBillboard(ctx, x, sBy, w, sBh, seed, _sNc, 0.78);
                        if (type === 'ticker')    _drawTickerAd(ctx, x, sBy, w, sBh, seed, _sNc, 0.80, assetTicker ? assetTickerEntry : null);
                    });
                }
                if (hasHolo)
                    _drawHologram(
                        ctx,
                        x,
                        topY,
                        w,
                        h,
                        seed,
                        CS_NEON[holoNeonIdx ?? neonIdx],
                        0.55
                    );
                if (hasHelipad) _drawHelipad(ctx, x, topY, w, seed);
            }
        );

        // Mid background
        bgMid.forEach(
            ({
                x,
                w,
                topY,
                colIdx,
                neonIdx,
                seed,
                shapeKind,
                hasSign,
                hasBillboard,
                hasTicker,
                hasLEDStrips,
                hasHolo,
                hasHelipad,
                signNeonIdx,
                ledNeonIdx,
                holoNeonIdx,
                assetTicker,
            }) => {
                const h = groundY - topY;
                const fc = CS_BLDG[colIdx];
                const nc = CS_NEON[neonIdx];
                ctx.fillStyle = `rgb(${fc[0]},${fc[1]},${fc[2]})`;
                _traceBgShape(ctx, x, topY, w, h, shapeKind, seed);
                ctx.fill();
                // Subtle blue-purple tint — mid buildings still slightly haze-tinted
                ctx.fillStyle = 'rgba(15,6,40,0.14)';
                _traceBgShape(ctx, x, topY, w, h, shapeKind, seed);
                ctx.fill();
                ctx.save();
                _traceBgShape(ctx, x, topY, w, h, shapeKind, seed);
                ctx.clip();
                ctx.strokeStyle = `rgba(${CS_ST[0]},${CS_ST[1]},${CS_ST[2]},0.09)`;
                ctx.lineWidth = 0.4;
                ctx.beginPath();
                for (let ly = topY + 8; ly < topY + h - 2; ly += 8) {
                    ctx.moveTo(x, ly);
                    ctx.lineTo(x + w, ly);
                }
                ctx.stroke();
                const dsx = Math.max(
                    (w - 4) / Math.max(1, Math.floor(w / 9)),
                    8
                );
                for (let dy = topY + 5; dy < topY + h - 4; dy += 8) {
                    for (let dx = x + 3; dx < x + w - 2; dx += dsx) {
                        if (
                            Math.abs(
                                Math.sin(seed * 37.1 + dy * 0.31 + dx * 0.19)
                            ) > 0.4
                        ) {
                            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.68)`;
                            ctx.fillRect(dx, dy, 3, 2);
                        }
                    }
                }
                ctx.restore();
                // Per-building neon-tinted outline
                const mBt = 0.25 + Math.abs(Math.sin(seed * 73.1)) * 0.4;
                const mBst = [
                    Math.round(CS_ST[0] * (1 - mBt) + nc[0] * mBt),
                    Math.round(CS_ST[1] * (1 - mBt) + nc[1] * mBt),
                    Math.round(CS_ST[2] * (1 - mBt) + nc[2] * mBt),
                ];
                ctx.save();
                ctx.shadowColor = `rgb(${nc[0]},${nc[1]},${nc[2]})`;
                ctx.shadowBlur = 5;
                ctx.strokeStyle = `rgba(${mBst[0]},${mBst[1]},${mBst[2]},0.55)`;
                ctx.lineWidth = 1.0;
                _traceBgShape(ctx, x, topY, w, h, shapeKind, seed);
                ctx.stroke();
                ctx.restore();
                if (hasLEDStrips)
                    _drawLEDStrips(
                        ctx,
                        x,
                        topY,
                        w,
                        h,
                        seed,
                        CS_NEON[ledNeonIdx ?? neonIdx],
                        0.65
                    );
                {
                    const _signs = [
                        hasSign && 'sign',
                        hasBillboard && 'billboard',
                        hasTicker && 'ticker',
                    ].filter(Boolean);
                    const _slotH = _signs.length > 1 ? h / _signs.length : h;
                    const _sNc = CS_NEON[signNeonIdx ?? neonIdx];
                    _signs.forEach((type, si) => {
                        const sBy = topY + si * _slotH;
                        const sBh = _slotH;
                        if (type === 'sign')      _drawBgSign(ctx, x, sBy, w, sBh, seed, _sNc, 0.88);
                        if (type === 'billboard') _drawLargeBillboard(ctx, x, sBy, w, sBh, seed + 33, _sNc, 0.92);
                        if (type === 'ticker')    _drawTickerAd(ctx, x, sBy, w, sBh, seed, _sNc, 0.90, assetTicker ? assetTickerEntry : null);
                    });
                }
                if (hasHolo)
                    _drawHologram(
                        ctx,
                        x,
                        topY,
                        w,
                        h,
                        seed,
                        CS_NEON[holoNeonIdx ?? neonIdx],
                        0.7
                    );
                if (hasHelipad) _drawHelipad(ctx, x, topY, w, seed);
            }
        );

        // Atmospheric mist near ground (depth separation)
        const mist = ctx.createLinearGradient(0, groundY * 0.65, 0, groundY);
        mist.addColorStop(0, 'rgba(18,6,38,0)');
        mist.addColorStop(1, 'rgba(18,6,38,0.38)');
        ctx.fillStyle = mist;
        ctx.fillRect(0, 0, W, groundY);

        // Wires (behind foreground buildings)
        wires.forEach(({ x1, y1, x2, y2, droop, neonIdx }) => {
            const mx = (x1 + x2) / 2;
            const my = Math.max(y1, y2) + droop;
            if (neonIdx >= 0) {
                const nc = CS_NEON[neonIdx];
                ctx.save();
                ctx.shadowColor = `rgb(${nc[0]},${nc[1]},${nc[2]})`;
                ctx.shadowBlur = 5;
                ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.65)`;
                ctx.lineWidth = 0.8;
            } else {
                ctx.strokeStyle = `rgba(${CS_ST[0]},${CS_ST[1]},${CS_ST[2]},0.32)`;
                ctx.lineWidth = 0.5;
            }
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.quadraticCurveTo(mx, my, x2, y2);
            ctx.stroke();
            if (neonIdx >= 0) ctx.restore();
        });

        // Foreground buildings
        buildings.forEach((b) => _drawBuilding(ctx, b));

        // Price-data powerlines — wire chain connecting adjacent column tops,
        // tracing the price silhouette as a visible neon line above the rooftops.
        if (priceWires.length > 0) {
            const pwNc = CS_NEON[priceWireNeon];
            ctx.save();

            // Insulator posts — masts from the exact price wire down to rooftops
            ctx.strokeStyle = `rgba(${CS_ST[0]},${CS_ST[1]},${CS_ST[2]},0.55)`;
            ctx.lineWidth = 0.9;
            priceWires.forEach(({ points }) => {
                points.forEach(({ x, y, supportY }) => {
                    if (!Number.isFinite(supportY)) return;
                    ctx.beginPath();
                    ctx.moveTo(x, y);
                    ctx.lineTo(x, supportY);
                    ctx.stroke();
                });
            });

            // Wire chain — sequential quadratic beziers with slight catenary droop
            ctx.shadowColor = `rgb(${pwNc[0]},${pwNc[1]},${pwNc[2]})`;
            ctx.shadowBlur = 7;
            ctx.strokeStyle = `rgba(${pwNc[0]},${pwNc[1]},${pwNc[2]},0.90)`;
            ctx.lineWidth = 1.2;
            priceWires.forEach(({ points }) => {
                if (points.length < 2) return;
                ctx.beginPath();
                ctx.moveTo(points[0].x, points[0].y);
                for (let i = 0; i < points.length - 1; i++) {
                    const a = points[i];
                    const b = points[i + 1];
                    const droop = Math.abs(b.x - a.x) * 0.05;
                    const mx = (a.x + b.x) / 2;
                    const my = (a.y + b.y) / 2 + droop;
                    ctx.quadraticCurveTo(mx, my, b.x, b.y);
                }
                ctx.stroke();
            });

            ctx.globalAlpha = 0.55;
            ctx.lineWidth = 0.9;
            priceWireBridges.forEach(({ points }) => {
                if (points.length < 2) return;
                ctx.beginPath();
                ctx.moveTo(points[0].x, points[0].y);
                for (let i = 1; i < points.length; i++) {
                    ctx.lineTo(points[i].x, points[i].y);
                }
                ctx.stroke();
            });

            ctx.restore();
        }

        // Skywalks
        skywalks.forEach((s) => _drawSkywalk(ctx, s));

        // Scaffold
        scaffolds.forEach((s) => _drawScaffold(ctx, s));

        // Hover cars
        hoverCars.forEach((c) => _drawHoverCar(ctx, c));

        // Ground fill
        const gndG = ctx.createLinearGradient(0, groundY, 0, H);
        gndG.addColorStop(0, '#0a0420');
        gndG.addColorStop(0.4, '#060218');
        gndG.addColorStop(1, '#030110');
        ctx.fillStyle = gndG;
        ctx.fillRect(0, groundY, W, H - groundY);

        // Ground line
        ctx.strokeStyle = `rgba(${CS_ST[0]},${CS_ST[1]},${CS_ST[2]},0.28)`;
        ctx.lineWidth = 1.0;
        ctx.beginPath();
        ctx.moveTo(0, groundY);
        ctx.lineTo(W, groundY);
        ctx.stroke();

        // Street horizontal lines
        ctx.strokeStyle = 'rgba(70,50,110,0.08)';
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        for (let hy = groundY + 8; hy < H; hy += 8) {
            ctx.moveTo(0, hy);
            ctx.lineTo(W, hy);
        }
        ctx.stroke();

        // Neon puddle reflections
        groundGlows.forEach(({ x, y, r, alpha, nc }) => {
            const rg = ctx.createRadialGradient(x, y, 0, x, y, r);
            rg.addColorStop(0, `rgba(${nc[0]},${nc[1]},${nc[2]},${alpha})`);
            rg.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.fillStyle = rg;
            ctx.fillRect(x - r, y - r, r * 2, r * 2);
        });

        // Building base glow
        const baseG = ctx.createLinearGradient(
            0,
            groundY,
            0,
            groundY + H * 0.09
        );
        baseG.addColorStop(0, 'rgba(80,20,130,0.24)');
        baseG.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = baseG;
        ctx.fillRect(0, groundY, W, H * 0.09);

        // Weather overlay — drawn last so it sits above all other elements
        if (weather) {
            ctx.save();
            ctx.globalAlpha = 1;
            if (weather.kind === 'fog') {
                // Gradient haze — heavier near ground
                const fogG = ctx.createLinearGradient(0, 0, 0, H);
                fogG.addColorStop(0,    'rgba(80,100,160,0)');
                fogG.addColorStop(0.55, `rgba(80,100,160,${weather.density * 0.3})`);
                fogG.addColorStop(0.82, `rgba(100,120,180,${weather.density})`);
                fogG.addColorStop(1,    `rgba(60,80,140,${weather.density * 0.8})`);
                ctx.fillStyle = fogG;
                ctx.fillRect(0, 0, W, H);
                // Radial wisps at varying heights
                weather.wisps.forEach(({ x, y, rx, ry, alpha }) => {
                    const wg = ctx.createRadialGradient(x, y, 0, x, y, rx);
                    wg.addColorStop(0,   `rgba(140,160,220,${alpha})`);
                    wg.addColorStop(0.5, `rgba(100,120,190,${alpha * 0.5})`);
                    wg.addColorStop(1,   'rgba(80,100,180,0)');
                    ctx.save();
                    ctx.translate(x, y);
                    ctx.scale(1, ry / rx);
                    ctx.fillStyle = wg;
                    ctx.beginPath();
                    ctx.arc(0, 0, rx, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.restore();
                });
            } else {
                // Atmospheric haze
                const hazeG = ctx.createLinearGradient(0, 0, 0, H);
                hazeG.addColorStop(0, `rgba(60,80,120,${weather.intensity * 0.08})`);
                hazeG.addColorStop(1, `rgba(40,60,100,${weather.intensity * 0.18})`);
                ctx.fillStyle = hazeG;
                ctx.fillRect(0, 0, W, H);
                // Rain streaks — batched by layer to minimise state changes
                const dx = Math.cos(Math.PI / 2 + 0.18);
                const dy = Math.sin(Math.PI / 2 + 0.18);
                weather.drops.forEach((layer) => {
                    const { alpha, lw } = layer[0];
                    ctx.globalAlpha = alpha;
                    ctx.strokeStyle = 'rgba(180,210,255,1)';
                    ctx.lineWidth = lw;
                    ctx.beginPath();
                    layer.forEach(({ x, y, len }) => {
                        ctx.moveTo(x, y);
                        ctx.lineTo(x + dx * len, y + dy * len);
                    });
                    ctx.stroke();
                });
                // Splash ellipses on ground
                ctx.globalAlpha = 1;
                ctx.strokeStyle = 'rgba(180,210,255,1)';
                ctx.lineWidth = 0.6;
                weather.splashes.forEach(({ x, y, rx, ry, alpha }) => {
                    ctx.globalAlpha = alpha;
                    ctx.beginPath();
                    ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
                    ctx.stroke();
                });
                // Wet street gloss
                ctx.globalAlpha = weather.intensity * 0.12;
                const wetG = ctx.createLinearGradient(0, groundY, 0, H);
                wetG.addColorStop(0, 'rgba(80,120,200,0.9)');
                wetG.addColorStop(1, 'rgba(40,70,140,0.4)');
                ctx.fillStyle = wetG;
                ctx.fillRect(0, groundY, W, H - groundY);
            }
            ctx.restore();
        }
    };

    // ── Moon (full phase + crater implementation) ─────────────────────────────
    function _drawMoon(ctx, cx, cy, r, phase, dir, craters) {
        // Atmospheric glow
        const glowG = ctx.createRadialGradient(
            cx,
            cy,
            r * 0.85,
            cx,
            cy,
            r * 4.5
        );
        glowG.addColorStop(0, 'rgba(220,235,255,0.22)');
        glowG.addColorStop(0.25, 'rgba(180,210,255,0.12)');
        glowG.addColorStop(0.55, 'rgba(100,140,240,0.05)');
        glowG.addColorStop(1, 'rgba(50,80,200,0)');
        ctx.fillStyle = glowG;
        ctx.fillRect(cx - r * 5, cy - r * 5, r * 10, r * 10);

        ctx.save();

        // Clip all moon rendering to the disc
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.clip();

        // Base moon color — bright blue-white
        ctx.fillStyle = 'rgb(238,245,255)';
        ctx.fillRect(cx - r, cy - r, r * 2, r * 2);

        // Sphere shading — lit from direction
        const lightX = cx + dir * r * 0.28;
        const lightY = cy - r * 0.18;
        const sphereG = ctx.createRadialGradient(
            lightX,
            lightY,
            r * 0.05,
            cx,
            cy,
            r * 1.02
        );
        sphereG.addColorStop(0, 'rgba(255,250,235,0.08)');
        sphereG.addColorStop(0.65, 'rgba(0,0,0,0)');
        sphereG.addColorStop(1, 'rgba(0,0,0,0.34)');
        ctx.fillStyle = sphereG;
        ctx.fillRect(cx - r, cy - r, r * 2, r * 2);

        // Craters
        const lightAngle = dir > 0 ? 0 : Math.PI;
        const shadowAngle = dir > 0 ? Math.PI : 0;
        craters.forEach(({ ox, oy, cr, depth }) => {
            const crx = cx + ox,
                cry = cy + oy;
            // Floor
            ctx.fillStyle = `rgba(0,0,0,${0.13 * depth})`;
            ctx.beginPath();
            ctx.arc(crx, cry, cr, 0, Math.PI * 2);
            ctx.fill();
            // Inner shadow rim
            ctx.strokeStyle = `rgba(0,0,0,${0.22 * depth})`;
            ctx.lineWidth = cr * 0.4;
            ctx.beginPath();
            ctx.arc(crx, cry, cr * 0.7, shadowAngle - 0.85, shadowAngle + 0.85);
            ctx.stroke();
            // Bright rim highlight
            ctx.strokeStyle = `rgba(255,252,230,${0.28 * depth})`;
            ctx.lineWidth = cr * 0.26;
            ctx.beginPath();
            ctx.arc(crx, cry, cr * 0.9, lightAngle - 0.75, lightAngle + 0.75);
            ctx.stroke();
        });

        // Phase shadow
        if (phase !== 'full') {
            ctx.save();
            if (phase === 'crescent') {
                ctx.beginPath();
                ctx.arc(cx - dir * r * 0.72, cy, r * 1.08, 0, Math.PI * 2);
                ctx.clip();
            } else if (phase === 'gibbous') {
                ctx.beginPath();
                ctx.arc(cx, cy, r, 0, Math.PI * 2);
                ctx.arc(
                    cx + dir * r * 0.28,
                    cy,
                    r * 0.94,
                    0,
                    Math.PI * 2,
                    true
                );
                ctx.clip('evenodd');
            } else if (phase === 'quarter') {
                ctx.beginPath();
                ctx.rect(dir > 0 ? cx - r : cx, cy - r, r, r * 2);
                ctx.clip();
            }
            // Shadow color — deep purple-blue matching the sky
            ctx.fillStyle = 'rgba(6,2,26,0.93)';
            ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
            ctx.restore();
        }

        ctx.restore();
    }

    // ── Foreground building module ────────────────────────────────────────────
    function _drawBuilding(
        ctx,
        {
            x,
            y,
            w,
            h,
            roofType,
            roofH,
            seedX,
            colIdx,
            neonIdx,
            hasSign,
            hasRoofLights,
            sbSections,
        }
    ) {
        const fc = CS_BLDG[colIdx];
        const nc = CS_NEON[neonIdx];
        // Per-building border color: CS_ST tinted 25–65% toward the building's neon hue
        const bt = 0.25 + Math.abs(Math.sin(seedX * 73.1)) * 0.4;
        const bst = [
            Math.round(CS_ST[0] * (1 - bt) + nc[0] * bt),
            Math.round(CS_ST[1] * (1 - bt) + nc[1] * bt),
            Math.round(CS_ST[2] * (1 - bt) + nc[2] * bt),
        ];

        // Solid fill
        ctx.fillStyle = `rgb(${fc[0]},${fc[1]},${fc[2]})`;
        ctx.fillRect(x, y, w, h);

        // Subtle floor-division lines
        const floorSpac = 10 + (seedX % 4);
        ctx.strokeStyle = `rgba(${bst[0]},${bst[1]},${bst[2]},0.08)`;
        ctx.lineWidth = 0.4;
        ctx.beginPath();
        for (let ly = y + floorSpac; ly < y + h - 1; ly += floorSpac) {
            ctx.moveTo(x + 0.5, ly);
            ctx.lineTo(x + w - 0.5, ly);
        }
        ctx.stroke();

        _drawWindows(ctx, x, y, w, h, seedX, neonIdx, 0.5);
        _drawLedges(ctx, x, y, w, h, seedX, bst);
        _drawPipes(ctx, x, y, w, h, seedX);
        _drawAcUnits(ctx, x, y, w, h, seedX, colIdx);
        if (hasSign) _drawNeonSign(ctx, x, y, w, h, seedX, neonIdx);

        // Outline
        const jx = _csJit(seedX, 0.4);
        const jy = _csJit(seedX + 1, 0.4);
        ctx.strokeStyle = `rgba(${bst[0]},${bst[1]},${bst[2]},0.60)`;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(x + jx, y + h + jy);
        ctx.lineTo(x + jx, y + jy);
        ctx.lineTo(x + w + jx, y + jy);
        ctx.lineTo(x + w + jx, y + h + jy);
        ctx.stroke();

        // Setback sections — drawn outside clip (they sit above the module, unaffected)
        if (sbSections && sbSections.length > 0) {
            sbSections.forEach((sb) => {
                ctx.fillStyle = `rgb(${Math.min(fc[0] + 6, 255)},${Math.min(fc[1] + 6, 255)},${Math.min(fc[2] + 8, 255)})`;
                ctx.fillRect(sb.x, sb.y, sb.w, sb.h);
                _drawWindows(
                    ctx,
                    sb.x,
                    sb.y,
                    sb.w,
                    sb.h,
                    seedX + 77,
                    neonIdx,
                    0.5
                );
                ctx.strokeStyle = `rgba(${bst[0]},${bst[1]},${bst[2]},0.55)`;
                ctx.lineWidth = 0.7;
                ctx.beginPath();
                ctx.moveTo(sb.x, sb.y + sb.h);
                ctx.lineTo(sb.x, sb.y);
                ctx.lineTo(sb.x + sb.w, sb.y);
                ctx.lineTo(sb.x + sb.w, sb.y + sb.h);
                ctx.stroke();
            });
        }

        if (roofH > 0 && roofType !== 0)
            _drawRoof(ctx, x, y, w, roofType, roofH, seedX, colIdx, bst);

        // Roof lights (on topmost module, drawn above roof)
        const topY =
            sbSections && sbSections.length > 0
                ? sbSections[sbSections.length - 1].y
                : y;
        const topW =
            sbSections && sbSections.length > 0
                ? sbSections[sbSections.length - 1].w
                : w;
        const topX =
            sbSections && sbSections.length > 0
                ? sbSections[sbSections.length - 1].x
                : x;
        if (hasRoofLights)
            _drawRoofLights(ctx, topX, topY, topW, seedX, neonIdx);
    }

    // ── Roof lights ───────────────────────────────────────────────────────────
    function _drawRoofLights(ctx, bx, by, bw, seed, neonIdx) {
        const nc = CS_NEON[neonIdx];
        const count = Math.max(1, Math.floor(bw / 10));
        ctx.save();
        ctx.shadowColor = `rgb(${nc[0]},${nc[1]},${nc[2]})`;
        ctx.shadowBlur = 6;
        for (let i = 0; i < count; i++) {
            if (Math.abs(Math.sin(seed * 29.3 + i * 17.7)) > 0.55) continue;
            const lx = bx + (i + 0.5) * (bw / count);
            const isRed = Math.abs(Math.sin(seed * 53.1 + i)) > 0.75;
            ctx.fillStyle = isRed
                ? 'rgba(255,60,60,0.92)'
                : `rgba(${nc[0]},${nc[1]},${nc[2]},0.90)`;
            ctx.beginPath();
            ctx.arc(lx, by - 1.5, 2, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.restore();
    }

    // ── Neon-lit windows ──────────────────────────────────────────────────────
    function _drawWindows(ctx, bx, by, bw, bh, seed, neonIdx, dimFactor = 1.0) {
        if (bw < 12 || bh < 16) return;
        const nc = CS_NEON[neonIdx];
        const winW = Math.min(bw * 0.18, 5);
        const winH = Math.min(bh * 0.2, 6);
        const rowH = 11;
        const rows = Math.max(1, Math.floor((bh - 4) / rowH));
        const cols = Math.max(1, Math.floor(bw / (winW + 4)));
        const colW = bw / cols;

        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                if (Math.abs(Math.sin(seed * 41.3 + r * 13.7 + c * 9.1)) > 0.58)
                    continue;
                if (
                    dimFactor < 1.0 &&
                    Math.abs(Math.sin(seed * 73.1 + r * 8.3 + c * 5.7)) >
                        dimFactor
                )
                    continue;
                const wx = bx + (c + 0.5) * colW - winW / 2;
                const wy = by + (r + 0.5) * rowH + 2;
                if (wy + winH > by + bh - 1) continue;
                ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.11)`;
                ctx.fillRect(wx - 2, wy - 2, winW + 4, winH + 4);
                ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.78)`;
                ctx.fillRect(wx, wy, winW, winH);
                ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.90)`;
                ctx.lineWidth = 0.5;
                ctx.beginPath();
                ctx.rect(wx, wy, winW, winH);
                ctx.stroke();
            }
        }
    }

    // ── Neon sign band ────────────────────────────────────────────────────────
    function _drawNeonSign(ctx, bx, by, bw, bh, seed, neonIdx) {
        if (bw < 14) return;
        const nc = CS_NEON[neonIdx];
        const signH = 4 + Math.abs(Math.sin(seed * 31.7)) * 4;
        const signW = bw * (0.5 + Math.abs(Math.sin(seed * 47.3)) * 0.4);
        const signX = bx + (bw - signW) / 2;
        const signY = by + Math.abs(Math.sin(seed * 23.1)) * (bh * 0.5) + 6;
        if (signY + signH > by + bh - 2) return;
        ctx.save();
        ctx.shadowColor = `rgb(${nc[0]},${nc[1]},${nc[2]})`;
        ctx.shadowBlur = 9;
        ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.82)`;
        ctx.fillRect(signX, signY, signW, signH);
        ctx.restore();
    }

    // ── Cornices / ledges ─────────────────────────────────────────────────────
    function _drawLedges(ctx, bx, by, bw, bh, seed, bst = CS_ST) {
        if (bh < 18) return;
        const slots = Math.floor(bh / 14);
        for (let i = 1; i < slots; i++) {
            if (Math.abs(Math.sin(seed * 57.1 + i * 19.3)) > 0.28) continue;
            const ly = by + i * (bh / slots);
            const ext = 2 + Math.abs(Math.sin(seed * 23.1 + i * 8.7)) * 4;
            ctx.strokeStyle = `rgba(${bst[0]},${bst[1]},${bst[2]},0.48)`;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(bx - ext, ly);
            ctx.lineTo(bx + bw + ext, ly);
            ctx.stroke();
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(bx - ext, ly);
            ctx.lineTo(bx - ext, ly + 2);
            ctx.moveTo(bx + bw + ext, ly);
            ctx.lineTo(bx + bw + ext, ly + 2);
            ctx.stroke();
        }
    }

    // ── Conduit pipes ─────────────────────────────────────────────────────────
    function _drawPipes(ctx, bx, by, bw, bh, seed) {
        if (bh < 24 || bw < 10) return;
        if (Math.abs(Math.sin(seed * 71.3)) > 0.3) return;
        const onLeft = Math.sin(seed * 43.7) > 0;
        const px = onLeft ? bx + 2.5 : bx + bw - 2.5;
        ctx.strokeStyle = `rgba(${CS_ST[0]},${CS_ST[1]},${CS_ST[2]},0.38)`;
        ctx.lineWidth = 1.3;
        ctx.beginPath();
        ctx.moveTo(px, by);
        ctx.lineTo(px, by + bh);
        ctx.stroke();
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        for (let jy = by + 10; jy < by + bh - 4; jy += 18) {
            ctx.moveTo(px - 2.5, jy);
            ctx.lineTo(px + 2.5, jy);
        }
        ctx.stroke();
    }

    // ── AC units ──────────────────────────────────────────────────────────────
    function _drawAcUnits(ctx, bx, by, bw, bh, seed, colIdx) {
        if (bw < 14 || bh < 14) return;
        const fc = CS_BLDG[colIdx];
        const unitH = 4 + Math.abs(Math.sin(seed * 37.1)) * 3;
        const unitW = 6 + Math.abs(Math.sin(seed * 51.3)) * 5;
        const slots = Math.floor(bh / 16);
        for (let i = 0; i < slots; i++) {
            if (Math.abs(Math.sin(seed * 83.1 + i * 21.7)) > 0.18) continue;
            const uy = by + i * 16 + 5;
            if (uy + unitH > by + bh - 2) continue;
            const onLeft = Math.sin(seed * 67.3 + i * 11.1) > 0;
            const ux = onLeft ? bx - unitW + 1 : bx + bw - 1;
            ctx.fillStyle = `rgb(${Math.min(fc[0] + 16, 255)},${Math.min(fc[1] + 16, 255)},${Math.min(fc[2] + 26, 255)})`;
            ctx.fillRect(ux, uy, unitW, unitH);
            ctx.strokeStyle = `rgba(${CS_ST[0]},${CS_ST[1]},${CS_ST[2]},0.48)`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.rect(ux, uy, unitW, unitH);
            ctx.stroke();
            ctx.beginPath();
            for (let vy = uy + 1.5; vy < uy + unitH - 0.5; vy += 1.5) {
                ctx.moveTo(ux + 0.5, vy);
                ctx.lineTo(ux + unitW - 0.5, vy);
            }
            ctx.stroke();
        }
    }

    // ── Roof shapes ───────────────────────────────────────────────────────────
    function _drawRoof(
        ctx,
        x,
        y,
        w,
        roofType,
        roofH,
        seed,
        colIdx,
        bst = CS_ST
    ) {
        const fc = CS_BLDG[colIdx];
        const jx = _csJit(seed + 2, 0.35);
        const jy = _csJit(seed + 3, 0.35);
        const str = `rgba(${bst[0]},${bst[1]},${bst[2]},0.65)`;
        const ov = Math.min(w * 0.07, 5);
        const fill = `rgb(${Math.max(fc[0] - 2, 0)},${Math.max(fc[1] - 2, 0)},${Math.min(fc[2] + 5, 255)})`;

        if (roofType === 1 || roofType === 2) {
            const highY = y - roofH,
                lowY = y - roofH * 0.12;
            const lY = roofType === 1 ? highY : lowY;
            const rY = roofType === 1 ? lowY : highY;
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(x - ov, y);
            ctx.lineTo(x - ov, lY);
            ctx.lineTo(x + w + ov, rY);
            ctx.lineTo(x + w + ov, y);
            ctx.closePath();
            ctx.fillStyle = fill;
            ctx.fill();
            ctx.clip();
            ctx.strokeStyle = `rgba(${CS_ST[0]},${CS_ST[1]},${CS_ST[2]},0.07)`;
            ctx.lineWidth = 0.4;
            ctx.beginPath();
            for (let ly = Math.min(lY, rY) + 3; ly < y; ly += 4) {
                ctx.moveTo(x - ov, ly);
                ctx.lineTo(x + w + ov, ly);
            }
            ctx.stroke();
            ctx.restore();
            ctx.strokeStyle = str;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(x - ov + jx, y + jy);
            ctx.lineTo(x - ov + jx, lY + jy);
            ctx.lineTo(x + w + ov + jx, rY + jy);
            ctx.lineTo(x + w + ov + jx, y + jy);
            ctx.stroke();
        } else if (roofType === 3) {
            const peak = y - roofH,
                cx = x + w / 2;
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(x - ov, y);
            ctx.lineTo(cx, peak);
            ctx.lineTo(x + w + ov, y);
            ctx.closePath();
            ctx.fillStyle = fill;
            ctx.fill();
            ctx.clip();
            ctx.strokeStyle = `rgba(${CS_ST[0]},${CS_ST[1]},${CS_ST[2]},0.07)`;
            ctx.lineWidth = 0.4;
            ctx.beginPath();
            for (let ly = peak + 3; ly < y; ly += 4) {
                ctx.moveTo(x - ov, ly);
                ctx.lineTo(x + w + ov, ly);
            }
            ctx.stroke();
            ctx.restore();
            ctx.strokeStyle = str;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(x - ov + jx, y + jy);
            ctx.lineTo(cx + jx, peak + jy);
            ctx.lineTo(x + w + ov + jx, y + jy);
            ctx.stroke();
        } else if (roofType === 4) {
            const cx = x + w / 2,
                rx = w / 2 + ov;
            ctx.save();
            ctx.beginPath();
            ctx.ellipse(cx, y, rx, roofH, 0, Math.PI, 0);
            ctx.closePath();
            ctx.fillStyle = fill;
            ctx.fill();
            ctx.clip();
            ctx.strokeStyle = `rgba(${CS_ST[0]},${CS_ST[1]},${CS_ST[2]},0.07)`;
            ctx.lineWidth = 0.4;
            ctx.beginPath();
            for (let ly = y - roofH + 4; ly < y; ly += 4) {
                ctx.moveTo(cx - rx, ly);
                ctx.lineTo(cx + rx, ly);
            }
            ctx.stroke();
            ctx.restore();
            ctx.strokeStyle = str;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.ellipse(cx + jx, y + jy, rx, roofH, 0, Math.PI, 0);
            ctx.stroke();
        } else if (roofType === 5) {
            const spireW = Math.min(w * 0.35, 10);
            const cx = x + w / 2;
            const peak = y - roofH * 2.2;
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(cx - spireW / 2, y);
            ctx.lineTo(cx, peak);
            ctx.lineTo(cx + spireW / 2, y);
            ctx.closePath();
            ctx.fillStyle = fill;
            ctx.fill();
            ctx.restore();
            ctx.strokeStyle = str;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(cx - spireW / 2 + jx, y + jy);
            ctx.lineTo(cx + jx, peak + jy);
            ctx.lineTo(cx + spireW / 2 + jx, y + jy);
            ctx.stroke();
            ctx.save();
            ctx.shadowColor = 'rgb(255,50,50)';
            ctx.shadowBlur = 7;
            ctx.fillStyle = 'rgba(255,80,80,0.90)';
            ctx.beginPath();
            ctx.arc(cx, peak, 1.5, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        }
    }

    // ── Skywalk bridge ────────────────────────────────────────────────────────
    function _drawSkywalk(ctx, { x1, x2, y, h, neonIdx, hasGlass }) {
        const nc = CS_NEON[neonIdx];
        const bridgeW = x2 - x1;
        if (bridgeW <= 0) return;

        // Glass tube body
        if (hasGlass) {
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.07)`;
            ctx.fillRect(x1, y, bridgeW, h);
        } else {
            ctx.fillStyle = 'rgb(42,40,50)';
            ctx.fillRect(x1, y, bridgeW, h);
        }

        // Top and bottom rails
        ctx.save();
        ctx.shadowColor = `rgb(${nc[0]},${nc[1]},${nc[2]})`;
        ctx.shadowBlur = 4;
        ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.75)`;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(x1, y);
        ctx.lineTo(x2, y);
        ctx.moveTo(x1, y + h);
        ctx.lineTo(x2, y + h);
        ctx.stroke();
        ctx.restore();

        // Vertical struts
        ctx.strokeStyle = `rgba(${CS_ST[0]},${CS_ST[1]},${CS_ST[2]},0.45)`;
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        const strutSpacing = Math.max(6, bridgeW / 5);
        for (let sx = x1 + strutSpacing; sx < x2 - 2; sx += strutSpacing) {
            ctx.moveTo(sx, y);
            ctx.lineTo(sx, y + h);
        }
        ctx.stroke();
    }

    // ── Hover car ─────────────────────────────────────────────────────────────
    function _drawHoverCar(ctx, { x, y, w, h, dir, neonIdx }) {
        const nc = CS_NEON[neonIdx];

        ctx.save();
        ctx.shadowColor = `rgb(${nc[0]},${nc[1]},${nc[2]})`;
        ctx.shadowBlur = 10;

        // Body — elongated teardrop shape
        const bx = dir > 0 ? x - w / 2 : x + w / 2;
        ctx.fillStyle = 'rgb(28,26,36)';
        ctx.beginPath();
        ctx.ellipse(x, y, w / 2, h / 2, 0, 0, Math.PI * 2);
        ctx.fill();

        // Canopy top
        ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.22)`;
        ctx.beginPath();
        ctx.ellipse(x, y - h * 0.15, w * 0.3, h * 0.4, 0, 0, Math.PI * 2);
        ctx.fill();

        // Underbelly glow strip
        const glowG = ctx.createLinearGradient(x - w * 0.4, y, x + w * 0.4, y);
        glowG.addColorStop(0, 'rgba(0,0,0,0)');
        glowG.addColorStop(0.5, `rgba(${nc[0]},${nc[1]},${nc[2]},0.60)`);
        glowG.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = glowG;
        ctx.fillRect(x - w * 0.45, y + h * 0.3, w * 0.9, h * 0.25);

        // Headlights
        const hx = dir > 0 ? x + w * 0.46 : x - w * 0.46;
        ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.95)`;
        ctx.beginPath();
        ctx.arc(hx, y - h * 0.05, 1.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(hx, y + h * 0.12, 1.5, 0, Math.PI * 2);
        ctx.fill();

        // Headlight beam
        ctx.save();
        ctx.shadowBlur = 0;
        const beamX = dir > 0 ? hx : hx - w * 0.6 * dir;
        const beamG = ctx.createLinearGradient(hx, y, hx + dir * w * 0.7, y);
        beamG.addColorStop(0, `rgba(${nc[0]},${nc[1]},${nc[2]},0.22)`);
        beamG.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = beamG;
        ctx.beginPath();
        ctx.moveTo(hx, y - h * 0.1);
        ctx.lineTo(hx + dir * w * 0.7, y - h * 0.3);
        ctx.lineTo(hx + dir * w * 0.7, y + h * 0.25);
        ctx.lineTo(hx, y + h * 0.18);
        ctx.closePath();
        ctx.fill();
        ctx.restore();

        ctx.restore();
    }

    // ── Scaffold (metal/structural) ───────────────────────────────────────────
    function _drawScaffold(ctx, { x, y, w, h }) {
        if (w < 4 || h < 4) return;
        const cellW = 16,
            cellH = 16;
        const cols = Math.max(1, Math.ceil(w / cellW));
        const rows = Math.max(1, Math.ceil(h / cellH));
        const right = x + cols * cellW;
        const bot = y + rows * cellH;
        ctx.strokeStyle = `rgba(${CS_ST[0]},${CS_ST[1]},${CS_ST[2]},0.38)`;
        ctx.lineWidth = 0.7;
        ctx.beginPath();
        for (let c = 0; c <= cols; c++) {
            const px = x + c * cellW;
            ctx.moveTo(px, y);
            ctx.lineTo(px, bot);
        }
        for (let r = 0; r <= rows; r++) {
            const ry = y + r * cellH;
            ctx.moveTo(x, ry);
            ctx.lineTo(right, ry);
        }
        ctx.stroke();
        ctx.strokeStyle = `rgba(${CS_ST[0]},${CS_ST[1]},${CS_ST[2]},0.24)`;
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        for (let c = 0; c < cols; c++) {
            for (let r = 0; r < rows; r++) {
                const x1 = x + c * cellW,
                    x2 = x + (c + 1) * cellW;
                const y1 = y + r * cellH,
                    y2 = y + (r + 1) * cellH;
                ctx.moveTo(x1, y1);
                ctx.lineTo(x2, y2);
                ctx.moveTo(x2, y1);
                ctx.lineTo(x1, y2);
            }
        }
        ctx.stroke();
    }

    // ── Large billboard sign — 6 styles + ambient glow ──────────────────────
    function _drawLargeBillboard(ctx, bx, by, bw, bh, seed, nc, alpha) {
        if (bw < 28 || bh < 35) return;

        // Ambient glow behind sign — drawn before panel so panel sits on top
        const glowCx = bx + bw / 2;
        const glowCy = by + bh * 0.3;
        const glowR = Math.max(bw * 0.85, bh * 0.55);
        const ambG = ctx.createRadialGradient(
            glowCx,
            glowCy,
            0,
            glowCx,
            glowCy,
            glowR
        );
        ambG.addColorStop(
            0,
            `rgba(${nc[0]},${nc[1]},${nc[2]},${(0.22 * alpha).toFixed(3)})`
        );
        ambG.addColorStop(
            0.38,
            `rgba(${nc[0]},${nc[1]},${nc[2]},${(0.09 * alpha).toFixed(3)})`
        );
        ambG.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.save();
        ctx.fillStyle = ambG;
        ctx.fillRect(bx - glowR, glowCy - glowR, glowR * 2 + bw, glowR * 2);
        ctx.restore();

        const styleIdx = Math.floor(Math.abs(Math.sin(seed * 97.3)) * 9);
        const word =
            _SIGN_WORDS[
                Math.floor(Math.abs(Math.sin(seed * 53.7)) * _SIGN_WORDS.length)
            ];
        const kanji =
            _KANJI_ONLY[
                Math.floor(Math.abs(Math.sin(seed * 71.1)) * _KANJI_ONLY.length)
            ];
        const isVertical =
            bh > bw * 1.6 ||
            (bw < 60 && Math.abs(Math.sin(seed * 29.1)) > 0.45);

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.shadowColor = `rgb(${nc[0]},${nc[1]},${nc[2]})`;

        if (styleIdx === 0 || isVertical) {
            // ── Style 0: vertical stacked text ───────────────────────────────
            const sW = Math.min(bw * 0.82, 56);
            const sH = Math.min(bh * 0.72, 300);
            const sX = bx + (bw - sW) / 2;
            const sY =
                by + bh * (0.04 + Math.abs(Math.sin(seed * 17.3)) * 0.12);
            if (sY + sH > by + bh - 6) {
                ctx.restore();
                return;
            }
            ctx.fillStyle = 'rgba(0,0,6,0.92)';
            ctx.fillRect(sX, sY, sW, sH);
            ctx.shadowBlur = 14;
            ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.95)`;
            ctx.lineWidth = 1.8;
            ctx.strokeRect(sX, sY, sW, sH);
            ctx.shadowBlur = 10;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.97)`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const charH = sH / Math.max(word.length, 1);
            const fontSize0 = Math.max(
                10,
                Math.min(sW * 0.72, charH * 0.85, 44)
            );
            ctx.font = `bold ${fontSize0}px monospace`;
            for (let i = 0; i < word.length; i++) {
                ctx.fillText(word[i], sX + sW / 2, sY + charH * (i + 0.5));
            }
        } else if (styleIdx === 1) {
            // ── Style 1: horizontal text ──────────────────────────────────────
            const sW = bw * (0.72 + Math.abs(Math.sin(seed * 23.1)) * 0.25);
            const sH = Math.min(bh * 0.44, Math.max(28, bw * 0.6), 110);
            const sX = bx + (bw - sW) / 2;
            const sY =
                by + bh * (0.05 + Math.abs(Math.sin(seed * 37.1)) * 0.28);
            if (sY + sH > by + bh - 4) {
                ctx.restore();
                return;
            }
            ctx.fillStyle = 'rgba(0,0,6,0.92)';
            ctx.fillRect(sX, sY, sW, sH);
            ctx.shadowBlur = 16;
            ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.95)`;
            ctx.lineWidth = 1.8;
            ctx.strokeRect(sX, sY, sW, sH);
            const fontSize1 = Math.max(
                10,
                Math.min((sW / Math.max(word.length, 1)) * 1.4, sH * 0.7, 48)
            );
            ctx.font = `bold ${fontSize1}px monospace`;
            ctx.shadowBlur = 8;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.96)`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(word, sX + sW / 2, sY + sH / 2);
        } else if (styleIdx === 2) {
            // ── Style 2: kanji monogram in concentric ring frame ──────────────
            const sz = Math.min(bw * 0.8, bh * 0.7, 120);
            if (sz < 24) {
                ctx.restore();
                return;
            }
            const cx = bx + bw / 2;
            const cy =
                by +
                sz * 0.55 +
                bh * (0.04 + Math.abs(Math.sin(seed * 17.3)) * 0.14);
            if (cy + sz * 0.55 > by + bh - 4) {
                ctx.restore();
                return;
            }
            const r1 = sz / 2;
            const r2 = r1 * 0.8;
            // Dark circular bg
            ctx.fillStyle = 'rgba(0,0,0,0.72)';
            ctx.beginPath();
            ctx.arc(cx, cy, r1 + 4, 0, Math.PI * 2);
            ctx.fill();
            // Outer ring
            ctx.shadowBlur = 14;
            ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.92)`;
            ctx.lineWidth = 1.6;
            ctx.beginPath();
            ctx.arc(cx, cy, r1, 0, Math.PI * 2);
            ctx.stroke();
            // Inner ring
            ctx.shadowBlur = 6;
            ctx.lineWidth = 0.75;
            ctx.beginPath();
            ctx.arc(cx, cy, r2, 0, Math.PI * 2);
            ctx.stroke();
            // Tick marks between rings
            ctx.lineWidth = 1;
            ctx.shadowBlur = 4;
            for (let t = 0; t < 8; t++) {
                const ang = (t / 8) * Math.PI * 2;
                ctx.beginPath();
                ctx.moveTo(cx + Math.cos(ang) * r2, cy + Math.sin(ang) * r2);
                ctx.lineTo(cx + Math.cos(ang) * r1, cy + Math.sin(ang) * r1);
                ctx.stroke();
            }
            // Kanji
            const kFont = Math.max(14, r2 * 1.05);
            ctx.font = `${kFont}px serif`;
            ctx.shadowBlur = 20;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},1.0)`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(kanji, cx, cy);
        } else if (styleIdx === 3) {
            // ── Style 3: LED dot matrix ───────────────────────────────────────
            const sW = Math.min(bw * 0.88, 130);
            const sH = Math.min(bh * 0.55, 85);
            const sX = bx + (bw - sW) / 2;
            const sY =
                by + bh * (0.06 + Math.abs(Math.sin(seed * 17.3)) * 0.25);
            if (sY + sH > by + bh - 4) {
                ctx.restore();
                return;
            }
            ctx.fillStyle = 'rgba(0,0,6,0.90)';
            ctx.fillRect(sX, sY, sW, sH);
            ctx.shadowBlur = 8;
            ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.70)`;
            ctx.lineWidth = 0.8;
            ctx.strokeRect(sX, sY, sW, sH);
            const dotR = 1.8;
            const dotSpX = Math.max(5, sW / 15);
            const dotSpY = Math.max(5, sH / 9);
            const cols = Math.floor((sW - 4) / dotSpX);
            const rows = Math.floor((sH - 4) / dotSpY);
            for (let r = 0; r < rows; r++) {
                for (let c = 0; c < cols; c++) {
                    const lit =
                        Math.abs(Math.sin(seed * 31.7 + r * 7.3 + c * 13.1)) >
                        0.38;
                    const dx = sX + 2 + dotSpX * (c + 0.5);
                    const dy = sY + 2 + dotSpY * (r + 0.5);
                    ctx.shadowBlur = lit ? 6 : 0;
                    ctx.fillStyle = lit
                        ? `rgba(${nc[0]},${nc[1]},${nc[2]},0.95)`
                        : `rgba(${nc[0]},${nc[1]},${nc[2]},0.11)`;
                    ctx.beginPath();
                    ctx.arc(dx, dy, dotR, 0, Math.PI * 2);
                    ctx.fill();
                }
            }
        } else if (styleIdx === 4) {
            // ── Style 4: geometric brand mark ────────────────────────────────
            const sW = Math.min(bw * 0.8, 100);
            const sH = Math.min(bh * 0.65, 95);
            const sX = bx + (bw - sW) / 2;
            const sY = by + bh * (0.05 + Math.abs(Math.sin(seed * 17.3)) * 0.2);
            if (sY + sH > by + bh - 4) {
                ctx.restore();
                return;
            }
            ctx.fillStyle = 'rgba(0,0,6,0.88)';
            ctx.fillRect(sX, sY, sW, sH);
            ctx.shadowBlur = 10;
            ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.80)`;
            ctx.lineWidth = 1.2;
            ctx.strokeRect(sX, sY, sW, sH);
            const gCx = sX + sW / 2;
            const gCy = sY + sH * 0.44;
            const geoR = Math.min(sW, sH) * 0.28;
            const gKind = Math.floor(Math.abs(Math.sin(seed * 43.7)) * 3);
            ctx.shadowBlur = 14;
            ctx.lineWidth = 1.5;
            if (gKind === 0) {
                // Diamond
                ctx.beginPath();
                ctx.moveTo(gCx, gCy - geoR);
                ctx.lineTo(gCx + geoR, gCy);
                ctx.lineTo(gCx, gCy + geoR);
                ctx.lineTo(gCx - geoR, gCy);
                ctx.closePath();
                ctx.stroke();
                ctx.lineWidth = 0.6;
                ctx.shadowBlur = 6;
                ctx.beginPath();
                ctx.moveTo(gCx, gCy - geoR * 0.52);
                ctx.lineTo(gCx + geoR * 0.52, gCy);
                ctx.lineTo(gCx, gCy + geoR * 0.52);
                ctx.lineTo(gCx - geoR * 0.52, gCy);
                ctx.closePath();
                ctx.stroke();
            } else if (gKind === 1) {
                // Equilateral triangle
                ctx.beginPath();
                ctx.moveTo(gCx, gCy - geoR);
                ctx.lineTo(gCx + geoR * 0.866, gCy + geoR * 0.5);
                ctx.lineTo(gCx - geoR * 0.866, gCy + geoR * 0.5);
                ctx.closePath();
                ctx.stroke();
                ctx.lineWidth = 0.6;
                ctx.shadowBlur = 6;
                const iR = geoR * 0.45;
                ctx.beginPath();
                ctx.moveTo(gCx, gCy - iR);
                ctx.lineTo(gCx + iR * 0.866, gCy + iR * 0.5);
                ctx.lineTo(gCx - iR * 0.866, gCy + iR * 0.5);
                ctx.closePath();
                ctx.stroke();
            } else {
                // Concentric circles with crosshairs
                [1, 0.65, 0.32].forEach((f, i) => {
                    ctx.lineWidth = i === 0 ? 1.5 : 0.7;
                    ctx.shadowBlur = i === 0 ? 14 : 6;
                    ctx.beginPath();
                    ctx.arc(gCx, gCy, geoR * f, 0, Math.PI * 2);
                    ctx.stroke();
                });
                ctx.lineWidth = 0.5;
                ctx.shadowBlur = 3;
                ctx.beginPath();
                ctx.moveTo(gCx - geoR * 0.28, gCy);
                ctx.lineTo(gCx + geoR * 0.28, gCy);
                ctx.moveTo(gCx, gCy - geoR * 0.28);
                ctx.lineTo(gCx, gCy + geoR * 0.28);
                ctx.stroke();
            }
            // Short label below shape
            const gLabel =
                _SIGN_WORDS[
                    Math.floor(
                        Math.abs(Math.sin(seed * 53.7)) * _SIGN_WORDS.length
                    )
                ];
            const gFontSize = Math.max(7, Math.min(sW * 0.17, 13));
            ctx.font = `${gFontSize}px monospace`;
            ctx.shadowBlur = 5;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.78)`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            ctx.fillText(gLabel, gCx, sY + sH * 0.76);
        } else if (styleIdx === 5) {
            // ── Style 5: glitch / distressed text (chromatic aberration) ─────
            const sW = bw * (0.72 + Math.abs(Math.sin(seed * 23.1)) * 0.25);
            const sH = Math.min(bh * 0.44, Math.max(28, bw * 0.6), 110);
            const sX = bx + (bw - sW) / 2;
            const sY =
                by + bh * (0.05 + Math.abs(Math.sin(seed * 37.1)) * 0.28);
            if (sY + sH > by + bh - 4) {
                ctx.restore();
                return;
            }
            ctx.fillStyle = 'rgba(0,0,6,0.92)';
            ctx.fillRect(sX, sY, sW, sH);
            ctx.shadowBlur = 8;
            ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.70)`;
            ctx.lineWidth = 1.2;
            ctx.strokeRect(sX, sY, sW, sH);
            const fontSize5 = Math.max(
                10,
                Math.min((sW / Math.max(word.length, 1)) * 1.4, sH * 0.7, 48)
            );
            const aberShift = Math.max(2, fontSize5 * 0.07);
            ctx.font = `bold ${fontSize5}px monospace`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const tCx = sX + sW / 2;
            const tCy = sY + sH / 2;
            // Red ghost
            ctx.shadowBlur = 0;
            ctx.fillStyle = 'rgba(255,20,60,0.42)';
            ctx.fillText(word, tCx + aberShift, tCy);
            // Cyan ghost
            ctx.fillStyle = 'rgba(0,220,220,0.38)';
            ctx.fillText(word, tCx - aberShift, tCy);
            // Main neon text
            ctx.shadowBlur = 10;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.96)`;
            ctx.fillText(word, tCx, tCy);
            // Horizontal scan-line cuts
            ctx.shadowBlur = 0;
            const cutCount =
                2 + Math.floor(Math.abs(Math.sin(seed * 59.3)) * 3);
            for (let cut = 0; cut < cutCount; cut++) {
                const cutY =
                    sY +
                    sH *
                        (0.15 +
                            Math.abs(Math.sin(seed * 41.7 + cut * 23.1)) * 0.7);
                const cutH = Math.max(2, fontSize5 * 0.12);
                ctx.fillStyle = 'rgba(0,0,0,0.62)';
                ctx.fillRect(sX + 1, cutY, sW - 2, cutH);
            }
        } else if (styleIdx === 6) {
            // ── Style 6: Blade Runner ticker board ───────────────────────────
            const tidx = Math.floor(
                Math.abs(Math.sin(seed * 109.3)) * _TICKER_ADS.length
            );
            const ticker = _TICKER_ADS[tidx];
            const sW = Math.min(bw * 0.92, 190);
            const sH = Math.min(bh * 0.72, 130);
            const sX = bx + (bw - sW) / 2;
            const sY =
                by + bh * (0.06 + Math.abs(Math.sin(seed * 17.3)) * 0.18);
            if (sY + sH > by + bh - 4) {
                ctx.restore();
                return;
            }
            const rawPrice = Math.abs(Math.sin(seed * 113.7)) * 100000;
            const priceStr =
                ticker.type === 'crypto'
                    ? rawPrice < 100
                        ? '$' + rawPrice.toFixed(2)
                        : '$' + Math.floor(rawPrice).toLocaleString()
                    : ticker.type === 'forex'
                      ? (0.6 + (rawPrice % 199.4)).toFixed(4)
                      : '$' + (10 + (rawPrice % 4990)).toFixed(2);
            const changePct = (Math.abs(Math.sin(seed * 73.1)) - 0.5) * 12;
            const isUp = changePct >= 0;
            const changeStr =
                (isUp ? '+' : '') + Math.abs(changePct).toFixed(1) + '%';
            const chNc = isUp ? [60, 255, 100] : [255, 60, 80];

            // Panel + double border
            ctx.fillStyle = 'rgba(0,2,8,0.94)';
            ctx.fillRect(sX, sY, sW, sH);
            ctx.save();
            ctx.beginPath(); ctx.rect(sX + 1, sY + 1, sW - 2, sH - 2); ctx.clip();
            ctx.shadowBlur = 14;
            ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.90)`;
            ctx.lineWidth = 1.4;
            ctx.strokeRect(sX, sY, sW, sH);
            ctx.shadowBlur = 4;
            ctx.lineWidth = 0.5;
            ctx.strokeRect(sX + 2.5, sY + 2.5, sW - 5, sH - 5);

            // Header bar tint
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.16)`;
            ctx.fillRect(sX + 1, sY + 1, sW - 2, sH * 0.22);

            ctx.textBaseline = 'middle';

            // Logo — top-right of header bar
            const s6Logo = _LOGO_ELEMENTS[ticker.sym] ?? null;
            const s6LogoSize = Math.min(sH * 0.18, 22);
            if (s6Logo) {
                ctx.save(); ctx.shadowBlur = 0;
                const s6LogoX = sX + sW - s6LogoSize - 5;
                const s6LogoY = sY + (sH * 0.22 - s6LogoSize) / 2 + 1;
                const r6 = s6LogoSize * 0.18;
                ctx.beginPath();
                ctx.moveTo(s6LogoX + r6, s6LogoY);
                ctx.arcTo(s6LogoX + s6LogoSize, s6LogoY, s6LogoX + s6LogoSize, s6LogoY + s6LogoSize, r6);
                ctx.arcTo(s6LogoX + s6LogoSize, s6LogoY + s6LogoSize, s6LogoX, s6LogoY + s6LogoSize, r6);
                ctx.arcTo(s6LogoX, s6LogoY + s6LogoSize, s6LogoX, s6LogoY, r6);
                ctx.arcTo(s6LogoX, s6LogoY, s6LogoX + s6LogoSize, s6LogoY, r6);
                ctx.closePath();
                ctx.clip();
                ctx.drawImage(s6Logo, s6LogoX, s6LogoY, s6LogoSize, s6LogoSize);
                ctx.restore();
            }

            // ▶ indicator + ticker symbol
            const symFont = Math.max(11, Math.min(sW * 0.2, sH * 0.26, 30));
            ctx.font = `bold ${symFont}px monospace`;
            ctx.shadowBlur = 14;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},1.0)`;
            ctx.textAlign = 'left';
            ctx.fillText('▶ ' + ticker.sym, sX + 7, sY + sH * 0.12);

            // Company name
            const nameFont = Math.max(6, Math.min(sW * 0.09, sH * 0.13, 13));
            ctx.font = `${nameFont}px monospace`;
            ctx.shadowBlur = 3;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.52)`;
            ctx.fillText(ticker.name, sX + 7, sY + sH * 0.36);

            // Type tag  [STOCK] / [CRYPTO] / [FOREX]
            const tag = '[' + ticker.type.toUpperCase() + ']';
            ctx.textAlign = 'right';
            ctx.fillText(tag, sX + sW - 7, sY + sH * 0.36);

            // Price
            const priceFont = Math.max(8, Math.min(sW * 0.13, sH * 0.18, 20));
            ctx.font = `bold ${priceFont}px monospace`;
            ctx.shadowBlur = 7;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.90)`;
            ctx.textAlign = 'left';
            ctx.fillText(priceStr, sX + 7, sY + sH * 0.56);

            // % change
            ctx.shadowColor = `rgb(${chNc[0]},${chNc[1]},${chNc[2]})`;
            ctx.shadowBlur = 9;
            ctx.fillStyle = `rgba(${chNc[0]},${chNc[1]},${chNc[2]},0.95)`;
            ctx.textAlign = 'right';
            ctx.fillText(changeStr, sX + sW - 7, sY + sH * 0.56);
            ctx.shadowColor = `rgb(${nc[0]},${nc[1]},${nc[2]})`;

            // Mini bar chart
            const barCount = Math.floor((sW - 14) / 7);
            const barMaxH = sH * 0.18;
            const barAreaY = sY + sH * 0.68;
            for (let bi = 0; bi < barCount; bi++) {
                const bUp2 = Math.sin(seed * 23.1 + bi * 11.3) > 0;
                const bh2 =
                    barMaxH * Math.abs(Math.sin(seed * 37.1 + bi * 17.3));
                const bc2 = bUp2 ? [60, 255, 100] : [255, 60, 80];
                ctx.shadowBlur = 3;
                ctx.strokeStyle = `rgba(${bc2[0]},${bc2[1]},${bc2[2]},0.70)`;
                ctx.lineWidth = 4;
                const bx2 = sX + 7 + bi * ((sW - 14) / barCount);
                ctx.beginPath();
                ctx.moveTo(bx2, barAreaY + barMaxH);
                ctx.lineTo(bx2, barAreaY + barMaxH - bh2);
                ctx.stroke();
            }

            // Scan lines
            ctx.shadowBlur = 0;
            ctx.fillStyle = 'rgba(0,0,0,0.16)';
            for (let sl = sY + 2; sl < sY + sH; sl += 3) {
                ctx.fillRect(sX + 1, sl, sW - 2, 1);
            }
            ctx.restore();
        } else if (styleIdx === 7) {
            // ── Style 7: Exchange advertisement ──────────────────────────────
            const tidx = Math.floor(
                Math.abs(Math.sin(seed * 109.3)) * _TICKER_ADS.length
            );
            const ticker = _TICKER_ADS[tidx];
            const sW = Math.min(bw * 0.88, 160);
            const sH = Math.min(bh * 0.74, 140);
            const sX = bx + (bw - sW) / 2;
            const sY =
                by + bh * (0.05 + Math.abs(Math.sin(seed * 37.1)) * 0.18);
            if (sY + sH > by + bh - 4) {
                ctx.restore();
                return;
            }
            const rawPrice = Math.abs(Math.sin(seed * 113.7)) * 100000;
            const changePct = (Math.abs(Math.sin(seed * 73.1)) - 0.5) * 12;
            const isUp7 = changePct >= 0;
            const changeStr7 =
                (isUp7 ? '+' : '') + Math.abs(changePct).toFixed(1) + '%';
            const chNc7 = isUp7 ? [60, 255, 100] : [255, 60, 80];

            ctx.fillStyle = 'rgba(0,1,6,0.96)';
            ctx.fillRect(sX, sY, sW, sH);
            ctx.save();
            ctx.beginPath(); ctx.rect(sX + 1, sY + 1, sW - 2, sH - 2); ctx.clip();
            ctx.shadowBlur = 16;
            ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.92)`;
            ctx.lineWidth = 1.8;
            ctx.strokeRect(sX, sY, sW, sH);

            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            // ★ BUY ★ SELL ★ header
            const hFont7 = Math.max(6, Math.min(sW * 0.11, 12));
            ctx.font = `bold ${hFont7}px monospace`;
            ctx.shadowBlur = 8;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.68)`;
            ctx.fillText('★  BUY  ★  SELL  ★', sX + sW / 2, sY + sH * 0.11);

            // Divider
            ctx.shadowBlur = 2;
            ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.28)`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(sX + 8, sY + sH * 0.21);
            ctx.lineTo(sX + sW - 8, sY + sH * 0.21);
            ctx.stroke();

            // Logo — small, left-aligned below divider
            const s7Logo = _LOGO_ELEMENTS[ticker.sym] ?? null;
            const s7LogoSize = Math.min(sH * 0.16, 20);
            if (s7Logo) {
                ctx.save(); ctx.shadowBlur = 0;
                ctx.drawImage(s7Logo, sX + 8, sY + sH * 0.25, s7LogoSize, s7LogoSize);
                ctx.restore();
            }

            // Big ticker symbol
            const bigFont7 = Math.max(14, Math.min(sW * 0.3, sH * 0.32, 50));
            ctx.font = `bold ${bigFont7}px monospace`;
            ctx.shadowBlur = 22;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},1.0)`;
            ctx.fillText(ticker.sym, sX + sW / 2, sY + sH * 0.46);

            // Mock yen price
            const yenPrice7 = Math.floor(
                rawPrice * (100 + Math.abs(Math.sin(seed * 17.3)) * 150)
            );
            const yFont7 = Math.max(7, Math.min(sW * 0.11, 14));
            ctx.font = `${yFont7}px monospace`;
            ctx.shadowBlur = 6;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.70)`;
            ctx.fillText(
                '¥' + yenPrice7.toLocaleString(),
                sX + sW / 2,
                sY + sH * 0.63
            );

            // % change
            ctx.shadowColor = `rgb(${chNc7[0]},${chNc7[1]},${chNc7[2]})`;
            ctx.shadowBlur = 10;
            ctx.fillStyle = `rgba(${chNc7[0]},${chNc7[1]},${chNc7[2]},0.96)`;
            const cfFont7 = Math.max(8, Math.min(sW * 0.13, 17));
            ctx.font = `bold ${cfFont7}px monospace`;
            ctx.fillText(changeStr7, sX + sW / 2, sY + sH * 0.76);
            ctx.shadowColor = `rgb(${nc[0]},${nc[1]},${nc[2]})`;

            // Japanese exchange name
            const jpIdx7 = Math.floor(
                Math.abs(Math.sin(seed * 31.7)) * _JP_EXCHANGE.length
            );
            const jpFont7 = Math.max(6, Math.min(sW * 0.09, 11));
            ctx.font = `${jpFont7}px monospace`;
            ctx.shadowBlur = 4;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.42)`;
            ctx.fillText(_JP_EXCHANGE[jpIdx7], sX + sW / 2, sY + sH * 0.89);

            // Scan lines
            ctx.shadowBlur = 0;
            ctx.fillStyle = 'rgba(0,0,0,0.14)';
            for (let sl = sY + 2; sl < sY + sH; sl += 3) {
                ctx.fillRect(sX + 1, sl, sW - 2, 1);
            }
            ctx.restore();
        } else {
            // ── Style 8: Multi-row data ticker strip ──────────────────────────
            const tidx = Math.floor(
                Math.abs(Math.sin(seed * 109.3)) * _TICKER_ADS.length
            );
            const rowH = Math.min(bh * 0.22, 22);
            const rowCount = Math.max(
                1,
                Math.min(3, Math.floor((bh * 0.65) / rowH))
            );
            const sW = Math.min(bw * 0.92, 210);
            const sH = rowCount * rowH + 10;
            const sX = bx + (bw - sW) / 2;
            const sY =
                by + bh * (0.06 + Math.abs(Math.sin(seed * 17.3)) * 0.22);
            if (sY + sH > by + bh - 4) {
                ctx.restore();
                return;
            }

            ctx.fillStyle = 'rgba(0,2,8,0.93)';
            ctx.fillRect(sX, sY, sW, sH);
            ctx.save();
            ctx.beginPath(); ctx.rect(sX + 1, sY + 1, sW - 2, sH - 2); ctx.clip();
            ctx.shadowBlur = 10;
            ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.85)`;
            ctx.lineWidth = 1.2;
            ctx.strokeRect(sX, sY, sW, sH);

            const rowFont8 = Math.max(7, Math.min(rowH * 0.58, sW * 0.08, 13));
            ctx.font = `bold ${rowFont8}px monospace`;
            ctx.textBaseline = 'middle';

            for (let ri = 0; ri < rowCount; ri++) {
                const t8 = _TICKER_ADS[(tidx + ri) % _TICKER_ADS.length];
                const rY = sY + 5 + ri * rowH + rowH / 2;
                const rSeed = seed + ri * 77.3;

                if (ri > 0) {
                    ctx.shadowBlur = 0;
                    ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.16)`;
                    ctx.lineWidth = 0.4;
                    ctx.beginPath();
                    ctx.moveTo(sX + 4, sY + 5 + ri * rowH);
                    ctx.lineTo(sX + sW - 4, sY + 5 + ri * rowH);
                    ctx.stroke();
                }

                // Ticker
                ctx.shadowColor = `rgb(${nc[0]},${nc[1]},${nc[2]})`;
                ctx.shadowBlur = 6;
                ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.95)`;
                ctx.textAlign = 'left';
                const s8LogoEl  = _LOGO_ELEMENTS[t8.sym] ?? null;
                const s8LogoSize = Math.min(rowH * 0.65, 14);
                if (s8LogoEl) {
                    ctx.save(); ctx.shadowBlur = 0;
                    ctx.drawImage(s8LogoEl, sX + 6, rY - s8LogoSize * 0.75, s8LogoSize, s8LogoSize);
                    ctx.restore();
                }
                ctx.fillText(t8.sym, sX + 6 + (s8LogoEl ? s8LogoSize + 3 : 0), rY);

                // Mock price
                const rRaw8 = Math.abs(Math.sin(rSeed * 113.7)) * 100000;
                const rPrice8 =
                    t8.type === 'crypto'
                        ? rRaw8 < 100
                            ? '$' + rRaw8.toFixed(2)
                            : '$' + Math.floor(rRaw8).toLocaleString()
                        : '$' + (10 + (rRaw8 % 4990)).toFixed(2);
                ctx.shadowBlur = 3;
                ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.58)`;
                ctx.textAlign = 'center';
                ctx.fillText(rPrice8, sX + sW / 2, rY);

                // % change
                const rPct8 = (Math.abs(Math.sin(rSeed * 73.1)) - 0.5) * 10;
                const rUp8 = rPct8 >= 0;
                const rNc8 = rUp8 ? [60, 255, 100] : [255, 60, 80];
                ctx.shadowColor = `rgb(${rNc8[0]},${rNc8[1]},${rNc8[2]})`;
                ctx.shadowBlur = 6;
                ctx.fillStyle = `rgba(${rNc8[0]},${rNc8[1]},${rNc8[2]},0.95)`;
                ctx.textAlign = 'right';
                ctx.fillText(
                    (rUp8 ? '+' : '') + Math.abs(rPct8).toFixed(1) + '%',
                    sX + sW - 6,
                    rY
                );
                ctx.shadowColor = `rgb(${nc[0]},${nc[1]},${nc[2]})`;
            }

            // Scan lines
            ctx.shadowBlur = 0;
            ctx.fillStyle = 'rgba(0,0,0,0.15)';
            for (let sl = sY + 2; sl < sY + sH; sl += 3) {
                ctx.fillRect(sX + 1, sl, sW - 2, 1);
            }
            ctx.restore();
        }

        ctx.restore();
    }

    // ── Helipad on rooftop — side view ───────────────────────────────────────
    // Seen from the side: a cantilevered platform at the roofline with a
    // hazard-striped face and red aviation lights at the corners.
    function _drawHelipad(ctx, bx, topY, bw, seed) {
        const overL = Math.abs(Math.sin(seed * 31.7)) * 7;
        const overR = Math.abs(Math.sin(seed * 47.3)) * 7;
        const padX = bx - overL;
        const padW = bw + overL + overR;
        const padH = Math.max(3, Math.min(padW * 0.14, 9));
        const padY = topY - padH;

        if (padW < 8) return;

        ctx.save();

        // Platform face
        ctx.fillStyle = 'rgba(34,32,42,0.98)';
        ctx.fillRect(padX, padY, padW, padH);

        // Diagonal hazard stripes clipped to the face
        ctx.save();
        ctx.beginPath();
        ctx.rect(padX, padY, padW, padH);
        ctx.clip();
        ctx.strokeStyle = 'rgba(215,185,30,0.82)';
        ctx.lineWidth = 2.2;
        const stripeSpacing = Math.max(6, padW / 7);
        for (
            let sx = padX - padH;
            sx < padX + padW + padH;
            sx += stripeSpacing
        ) {
            ctx.beginPath();
            ctx.moveTo(sx, padY);
            ctx.lineTo(sx + padH * 1.4, padY + padH);
            ctx.stroke();
        }
        ctx.restore();

        // Top edge — the visible rim of the landing surface
        ctx.strokeStyle = 'rgba(195,190,220,0.70)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(padX, padY);
        ctx.lineTo(padX + padW, padY);
        ctx.stroke();

        // Bottom edge
        ctx.strokeStyle = 'rgba(70,65,90,0.55)';
        ctx.lineWidth = 0.6;
        ctx.beginPath();
        ctx.moveTo(padX, padY + padH);
        ctx.lineTo(padX + padW, padY + padH);
        ctx.stroke();

        // Red aviation lights — left corner, right corner, centre
        const lightR = Math.max(1.5, padH * 0.3);
        ctx.save();
        ctx.shadowColor = 'rgb(255,30,30)';
        ctx.shadowBlur = 8;
        ctx.fillStyle = 'rgba(255,45,45,0.95)';
        [padX, padX + padW / 2, padX + padW].forEach((lx) => {
            ctx.beginPath();
            ctx.arc(lx, padY, lightR, 0, Math.PI * 2);
            ctx.fill();
        });
        ctx.restore();

        ctx.restore();
    }

    // ── Background building sign with alien script ────────────────────────────
    // alpha: master opacity multiplier (bgFar = 0.68, bgMid = 0.80)
    function _drawBgSign(ctx, bx, by, bw, bh, seed, nc, alpha) {
        if (bw < 22 || bh < 18) return;
        const glyphW = 4,
            glyphH = 7,
            pad = 3,
            spacing = 7;
        const rows = Math.abs(Math.sin(seed * 7.3)) > 0.6 ? 2 : 1;
        const maxG = Math.floor((bw - pad * 2) / spacing);
        if (maxG < 3) return;
        const glyphCount = Math.max(
            3,
            Math.floor(maxG * (0.45 + Math.abs(Math.sin(seed * 13.7)) * 0.5))
        );
        const signW = glyphCount * spacing + pad * 2;
        const signH = rows * (glyphH + 3) + pad * 2;
        const signX = bx + (bw - signW) / 2;
        const signY = by + bh * (0.08 + Math.abs(Math.sin(seed * 17.1)) * 0.4);
        if (signY + signH > by + bh - 4) return;

        // Ambient glow behind the sign panel
        const bgGlowCx = signX + signW / 2;
        const bgGlowCy = signY + signH / 2;
        const bgGlowR = Math.max(signW, signH) * 1.4;
        const bgAmbG = ctx.createRadialGradient(
            bgGlowCx,
            bgGlowCy,
            0,
            bgGlowCx,
            bgGlowCy,
            bgGlowR
        );
        bgAmbG.addColorStop(
            0,
            `rgba(${nc[0]},${nc[1]},${nc[2]},${(0.18 * alpha).toFixed(3)})`
        );
        bgAmbG.addColorStop(
            0.45,
            `rgba(${nc[0]},${nc[1]},${nc[2]},${(0.07 * alpha).toFixed(3)})`
        );
        bgAmbG.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.save();
        ctx.fillStyle = bgAmbG;
        ctx.fillRect(
            bgGlowCx - bgGlowR,
            bgGlowCy - bgGlowR,
            bgGlowR * 2,
            bgGlowR * 2
        );
        ctx.restore();

        ctx.save();
        ctx.globalAlpha = alpha;

        // Panel
        ctx.fillStyle = 'rgba(0,0,0,0.75)';
        ctx.fillRect(signX, signY, signW, signH);

        // Border glow
        ctx.shadowColor = `rgb(${nc[0]},${nc[1]},${nc[2]})`;
        ctx.shadowBlur = 5;
        ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.82)`;
        ctx.lineWidth = 0.7;
        ctx.beginPath();
        ctx.rect(signX, signY, signW, signH);
        ctx.stroke();

        // Glyphs
        ctx.shadowBlur = 3;
        ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.90)`;
        ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.90)`;
        ctx.lineWidth = 0.65;
        for (let row = 0; row < rows; row++) {
            const gy = signY + pad + row * (glyphH + 3);
            for (let i = 0; i < glyphCount; i++) {
                const gx = signX + pad + i * spacing;
                const idx = Math.floor(
                    Math.abs(Math.sin(seed * 31.7 + i * 13.1 + row * 7.3)) *
                        _ALIEN_GLYPHS.length
                );
                _ALIEN_GLYPHS[idx](ctx, gx, gy, glyphW, glyphH);
            }
        }

        ctx.restore();
    }

    // ── Blade Runner ticker advertisement ────────────────────────────────────
    // Three sub-styles chosen by seed: ticker board, exchange ad, data strip.
    function _drawTickerAd(ctx, bx, by, bw, bh, seed, nc, alpha, forcedTicker = null) {
        if (bw < 52 || bh < 28) return;

        const tidx    = Math.floor(Math.abs(Math.sin(seed * 109.3)) * _TICKER_ADS.length);
        const ticker  = forcedTicker ?? _TICKER_ADS[tidx];
        const adStyle = forcedTicker ? 0 : Math.floor(Math.abs(Math.sin(seed * 83.7)) * 3);

        const _logoEl = _LOGO_ELEMENTS[ticker.sym] ?? null;

        const _drawLogo = (x, y, size) => {
            if (!_logoEl) return;
            ctx.save();
            ctx.shadowBlur = 0;
            // Rounded clip for a polished look
            const r = size * 0.18;
            ctx.beginPath();
            ctx.moveTo(x + r, y);
            ctx.arcTo(x + size, y, x + size, y + size, r);
            ctx.arcTo(x + size, y + size, x, y + size, r);
            ctx.arcTo(x, y + size, x, y, r);
            ctx.arcTo(x, y, x + size, y, r);
            ctx.closePath();
            ctx.clip();
            ctx.drawImage(_logoEl, x, y, size, size);
            ctx.restore();
        };

        // Shared price/change data (seeded, deterministic)
        const rawPrice  = Math.abs(Math.sin(seed * 113.7)) * 100000;
        const priceStr  = ticker.type === 'crypto'
            ? (rawPrice < 100 ? '$' + rawPrice.toFixed(2) : '$' + Math.floor(rawPrice).toLocaleString())
            : ticker.type === 'forex' ? (0.60 + (rawPrice % 199.4)).toFixed(4)
            : '$' + (10 + (rawPrice % 4990)).toFixed(2);
        const changePct = (Math.abs(Math.sin(seed * 73.1)) - 0.5) * 12;
        const isUp      = changePct >= 0;
        const changeStr = (isUp ? '+' : '') + Math.abs(changePct).toFixed(1) + '%';
        const chNc      = isUp ? [60, 255, 100] : [255, 60, 80];

        // Ambient glow behind the panel
        const glowCx = bx + bw / 2, glowCy = by + bh * 0.30;
        const glowR  = Math.max(bw * 0.85, bh * 0.55);
        const ambG   = ctx.createRadialGradient(glowCx, glowCy, 0, glowCx, glowCy, glowR);
        ambG.addColorStop(0,    `rgba(${nc[0]},${nc[1]},${nc[2]},${(0.22 * alpha).toFixed(3)})`);
        ambG.addColorStop(0.40, `rgba(${nc[0]},${nc[1]},${nc[2]},${(0.08 * alpha).toFixed(3)})`);
        ambG.addColorStop(1,    'rgba(0,0,0,0)');
        ctx.save();
        ctx.fillStyle = ambG;
        ctx.fillRect(bx - glowR, glowCy - glowR, glowR * 2 + bw, glowR * 2);
        ctx.restore();

        ctx.save();
        ctx.globalAlpha  = alpha;
        ctx.shadowColor  = `rgb(${nc[0]},${nc[1]},${nc[2]})`;
        ctx.textBaseline = 'middle';

        const _scanLines = (sx, sy, sw, sh) => {
            ctx.shadowBlur = 0;
            ctx.fillStyle  = 'rgba(0,0,0,0.15)';
            for (let sl = sy + 2; sl < sy + sh; sl += 3) ctx.fillRect(sx + 1, sl, sw - 2, 1);
        };

        if (adStyle === 0) {
            // ── Ticker board ──────────────────────────────────────────────────
            const sW = Math.min(bw * 0.92, 190), sH = Math.min(bh * 0.72, 130);
            const sX = bx + (bw - sW) / 2;
            const sY = by + bh * (0.06 + Math.abs(Math.sin(seed * 17.3)) * 0.18);
            if (sY + sH > by + bh - 4) { ctx.restore(); return; }

            ctx.fillStyle = 'rgba(0,2,8,0.94)';
            ctx.fillRect(sX, sY, sW, sH);
            ctx.save();
            ctx.beginPath(); ctx.rect(sX + 1, sY + 1, sW - 2, sH - 2); ctx.clip();
            ctx.shadowBlur  = 14; ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.90)`; ctx.lineWidth = 1.4;
            ctx.strokeRect(sX, sY, sW, sH);
            ctx.shadowBlur  = 4;  ctx.lineWidth = 0.5;
            ctx.strokeRect(sX + 2.5, sY + 2.5, sW - 5, sH - 5);
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.16)`;
            ctx.fillRect(sX + 1, sY + 1, sW - 2, sH * 0.22);

            // Logo icon — top-right of header bar
            const logoSize0 = Math.min(sH * 0.18, 22);
            _drawLogo(sX + sW - logoSize0 - 5, sY + (sH * 0.22 - logoSize0) / 2 + 1, logoSize0);

            const symFont = Math.max(11, Math.min(sW * 0.20, sH * 0.26, 30));
            ctx.font = `bold ${symFont}px monospace`; ctx.shadowBlur = 14;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},1.0)`; ctx.textAlign = 'left';
            ctx.fillText('▶ ' + ticker.sym, sX + 7, sY + sH * 0.12);

            const nameFont = Math.max(6, Math.min(sW * 0.09, 13));
            ctx.font = `${nameFont}px monospace`; ctx.shadowBlur = 3;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.50)`;
            ctx.fillText(ticker.name, sX + 7, sY + sH * 0.36);
            ctx.textAlign = 'right';
            ctx.fillText('[' + ticker.type.toUpperCase() + ']', sX + sW - 7, sY + sH * 0.36);

            const pFont = Math.max(8, Math.min(sW * 0.13, 20));
            ctx.font = `bold ${pFont}px monospace`; ctx.shadowBlur = 7;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.90)`; ctx.textAlign = 'left';
            ctx.fillText(priceStr, sX + 7, sY + sH * 0.56);
            ctx.shadowColor = `rgb(${chNc[0]},${chNc[1]},${chNc[2]})`; ctx.shadowBlur = 9;
            ctx.fillStyle   = `rgba(${chNc[0]},${chNc[1]},${chNc[2]},0.95)`; ctx.textAlign = 'right';
            ctx.fillText(changeStr, sX + sW - 7, sY + sH * 0.56);
            ctx.shadowColor = `rgb(${nc[0]},${nc[1]},${nc[2]})`;

            const barCount = Math.floor((sW - 14) / 7), barMaxH = sH * 0.18, barAreaY = sY + sH * 0.68;
            for (let bi = 0; bi < barCount; bi++) {
                const bUp = Math.sin(seed * 23.1 + bi * 11.3) > 0;
                const bh2 = barMaxH * Math.abs(Math.sin(seed * 37.1 + bi * 17.3));
                const bc  = bUp ? [60, 255, 100] : [255, 60, 80];
                ctx.shadowBlur  = 3; ctx.strokeStyle = `rgba(${bc[0]},${bc[1]},${bc[2]},0.70)`; ctx.lineWidth = 4;
                const bx2 = sX + 7 + bi * ((sW - 14) / barCount);
                ctx.beginPath(); ctx.moveTo(bx2, barAreaY + barMaxH); ctx.lineTo(bx2, barAreaY + barMaxH - bh2); ctx.stroke();
            }
            _scanLines(sX, sY, sW, sH);
            ctx.restore();

        } else if (adStyle === 1) {
            // ── Exchange advertisement ────────────────────────────────────────
            const sW = Math.min(bw * 0.88, 160), sH = Math.min(bh * 0.74, 140);
            const sX = bx + (bw - sW) / 2;
            const sY = by + bh * (0.05 + Math.abs(Math.sin(seed * 37.1)) * 0.18);
            if (sY + sH > by + bh - 4) { ctx.restore(); return; }

            ctx.fillStyle = 'rgba(0,1,6,0.96)';
            ctx.fillRect(sX, sY, sW, sH);
            ctx.save();
            ctx.beginPath(); ctx.rect(sX + 1, sY + 1, sW - 2, sH - 2); ctx.clip();
            ctx.shadowBlur = 16; ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.92)`; ctx.lineWidth = 1.8;
            ctx.strokeRect(sX, sY, sW, sH);

            ctx.textAlign = 'center';
            const hFont = Math.max(6, Math.min(sW * 0.11, 12));
            ctx.font = `bold ${hFont}px monospace`; ctx.shadowBlur = 8;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.68)`;
            ctx.fillText('★  BUY  ★  SELL  ★', sX + sW / 2, sY + sH * 0.11);

            ctx.shadowBlur = 2; ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.28)`; ctx.lineWidth = 0.5;
            ctx.beginPath(); ctx.moveTo(sX + 8, sY + sH * 0.21); ctx.lineTo(sX + sW - 8, sY + sH * 0.21); ctx.stroke();

            const logoSize1 = Math.min(sH * 0.16, 20);
            _drawLogo(sX + 8, sY + sH * 0.25, logoSize1);

            const bigFont = Math.max(14, Math.min(sW * 0.30, sH * 0.32, 50));
            ctx.font = `bold ${bigFont}px monospace`; ctx.shadowBlur = 22;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},1.0)`;
            ctx.fillText(ticker.sym, sX + sW / 2, sY + sH * 0.46);

            const yenPrice = Math.floor(rawPrice * (100 + Math.abs(Math.sin(seed * 17.3)) * 150));
            const yFont    = Math.max(7, Math.min(sW * 0.11, 14));
            ctx.font = `${yFont}px monospace`; ctx.shadowBlur = 6;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.70)`;
            ctx.fillText('¥' + yenPrice.toLocaleString(), sX + sW / 2, sY + sH * 0.63);

            ctx.shadowColor = `rgb(${chNc[0]},${chNc[1]},${chNc[2]})`; ctx.shadowBlur = 10;
            ctx.fillStyle   = `rgba(${chNc[0]},${chNc[1]},${chNc[2]},0.96)`;
            const cfFont = Math.max(8, Math.min(sW * 0.13, 17));
            ctx.font = `bold ${cfFont}px monospace`;
            ctx.fillText(changeStr, sX + sW / 2, sY + sH * 0.76);
            ctx.shadowColor = `rgb(${nc[0]},${nc[1]},${nc[2]})`;

            const jpIdx = Math.floor(Math.abs(Math.sin(seed * 31.7)) * _JP_EXCHANGE.length);
            const jpFont = Math.max(6, Math.min(sW * 0.09, 11));
            ctx.font = `${jpFont}px monospace`; ctx.shadowBlur = 4;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.42)`;
            ctx.fillText(_JP_EXCHANGE[jpIdx], sX + sW / 2, sY + sH * 0.89);
            _scanLines(sX, sY, sW, sH);
            ctx.restore();

        } else {
            // ── Multi-row data strip ──────────────────────────────────────────
            const rowH     = Math.min(bh * 0.22, 22);
            const rowCount = Math.max(1, Math.min(3, Math.floor((bh * 0.65) / rowH)));
            const sW = Math.min(bw * 0.92, 210), sH = rowCount * rowH + 10;
            const sX = bx + (bw - sW) / 2;
            const sY = by + bh * (0.06 + Math.abs(Math.sin(seed * 17.3)) * 0.22);
            if (sY + sH > by + bh - 4) { ctx.restore(); return; }

            ctx.fillStyle = 'rgba(0,2,8,0.93)'; ctx.fillRect(sX, sY, sW, sH);
            ctx.save();
            ctx.beginPath(); ctx.rect(sX + 1, sY + 1, sW - 2, sH - 2); ctx.clip();
            ctx.shadowBlur = 10; ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.85)`; ctx.lineWidth = 1.2;
            ctx.strokeRect(sX, sY, sW, sH);

            const rowFont = Math.max(7, Math.min(rowH * 0.58, sW * 0.08, 13));
            ctx.font = `bold ${rowFont}px monospace`;

            for (let ri = 0; ri < rowCount; ri++) {
                const t8    = _TICKER_ADS[(tidx + ri) % _TICKER_ADS.length];
                const rY    = sY + 5 + ri * rowH + rowH / 2;
                const rSeed = seed + ri * 77.3;

                if (ri > 0) {
                    ctx.shadowBlur = 0; ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.16)`; ctx.lineWidth = 0.4;
                    ctx.beginPath(); ctx.moveTo(sX + 4, sY + 5 + ri * rowH); ctx.lineTo(sX + sW - 4, sY + 5 + ri * rowH); ctx.stroke();
                }
                ctx.shadowColor = `rgb(${nc[0]},${nc[1]},${nc[2]})`; ctx.shadowBlur = 6;
                ctx.fillStyle   = `rgba(${nc[0]},${nc[1]},${nc[2]},0.95)`; ctx.textAlign = 'left';
                const rLogoEl  = _LOGO_ELEMENTS[t8.sym] ?? null;
                const rLogoSize = Math.min(rowH * 0.65, 14);
                if (rLogoEl) {
                    ctx.save(); ctx.shadowBlur = 0;
                    ctx.drawImage(rLogoEl, sX + 6, rY - rLogoSize * 0.75, rLogoSize, rLogoSize);
                    ctx.restore();
                }
                ctx.fillText(t8.sym, sX + 6 + (rLogoEl ? rLogoSize + 3 : 0), rY);

                const rRaw   = Math.abs(Math.sin(rSeed * 113.7)) * 100000;
                const rPrice = t8.type === 'crypto'
                    ? (rRaw < 100 ? '$' + rRaw.toFixed(2) : '$' + Math.floor(rRaw).toLocaleString())
                    : '$' + (10 + (rRaw % 4990)).toFixed(2);
                ctx.shadowBlur = 3; ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.58)`; ctx.textAlign = 'center';
                ctx.fillText(rPrice, sX + sW / 2, rY);

                const rPct = (Math.abs(Math.sin(rSeed * 73.1)) - 0.5) * 10;
                const rUp  = rPct >= 0;
                const rNc  = rUp ? [60, 255, 100] : [255, 60, 80];
                ctx.shadowColor = `rgb(${rNc[0]},${rNc[1]},${rNc[2]})`; ctx.shadowBlur = 6;
                ctx.fillStyle   = `rgba(${rNc[0]},${rNc[1]},${rNc[2]},0.95)`; ctx.textAlign = 'right';
                ctx.fillText((rUp ? '+' : '') + Math.abs(rPct).toFixed(1) + '%', sX + sW - 6, rY);
                ctx.shadowColor = `rgb(${nc[0]},${nc[1]},${nc[2]})`;
            }
            _scanLines(sX, sY, sW, sH);
            ctx.restore();
        }

        ctx.restore();
    }

    // ── LED strip lighting along structural edges ─────────────────────────────
    function _drawLEDStrips(ctx, bx, by, bw, bh, seed, nc, alpha) {
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.shadowColor = `rgb(${nc[0]},${nc[1]},${nc[2]})`;
        ctx.shadowBlur = 4;
        ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.70)`;

        // Top edge
        if (Math.abs(Math.sin(seed * 11.3)) > 0.28) {
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(bx, by);
            ctx.lineTo(bx + bw, by);
            ctx.stroke();
        }

        // Vertical edge strips
        if (Math.abs(Math.sin(seed * 53.7)) > 0.48) {
            ctx.lineWidth = 0.7;
            ctx.beginPath();
            ctx.moveTo(bx + 1, by);
            ctx.lineTo(bx + 1, by + bh);
            ctx.stroke();
        }
        if (Math.abs(Math.sin(seed * 61.3)) > 0.48) {
            ctx.lineWidth = 0.7;
            ctx.beginPath();
            ctx.moveTo(bx + bw - 1, by);
            ctx.lineTo(bx + bw - 1, by + bh);
            ctx.stroke();
        }

        // Horizontal bands at random floor intervals
        ctx.shadowBlur = 3;
        ctx.lineWidth = 0.5;
        const bandCount = Math.max(1, Math.floor(bh / 18));
        for (let i = 1; i < bandCount; i++) {
            if (Math.abs(Math.sin(seed * 43.1 + i * 17.7)) > 0.62) continue;
            const ly = by + i * (bh / bandCount);
            ctx.beginPath();
            ctx.moveTo(bx, ly);
            ctx.lineTo(bx + bw, ly);
            ctx.stroke();
        }

        // Window-outline strip: a thin continuous horizontal at each window-row level
        const winRowH = 11;
        const winRows = Math.floor((bh - 4) / winRowH);
        ctx.shadowBlur = 2;
        ctx.lineWidth = 0.4;
        ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.40)`;
        for (let r = 0; r < winRows; r++) {
            if (Math.abs(Math.sin(seed * 79.3 + r * 23.1)) > 0.72) continue;
            const wy = by + (r + 0.5) * winRowH + 2;
            ctx.beginPath();
            ctx.moveTo(bx + 2, wy - 1);
            ctx.lineTo(bx + bw - 2, wy - 1);
            ctx.stroke();
        }

        ctx.restore();
    }

    // ── Holographic projection floating off building facade ───────────────────
    function _drawHologram(ctx, bx, by, bw, bh, seed, nc, alpha) {
        if (bw < 28 || bh < 28) return;
        const holoW = Math.min(
            bw * 0.75,
            18 + Math.abs(Math.sin(seed * 19.3)) * 30
        );
        const holoH = 12 + Math.abs(Math.sin(seed * 23.7)) * 22;
        const holoFrac = 0.18 + Math.abs(Math.sin(seed * 37.1)) * 0.46;
        const baseY = by + bh * holoFrac;
        const side = Math.sin(seed * 71.3) > 0 ? 1 : -1;
        const offset = 6 + Math.abs(Math.sin(seed * 41.1)) * 14;
        const hx = side > 0 ? bx + bw + offset : bx - offset - holoW;
        const hy = baseY - holoH / 2;
        if (hx < -holoW * 2 || hx > bx + bw * 3) return;

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.shadowColor = `rgb(${nc[0]},${nc[1]},${nc[2]})`;

        // Projection beam from facade
        const beamX = side > 0 ? bx + bw : bx;
        const beamTX = side > 0 ? hx : hx + holoW;
        ctx.shadowBlur = 0;
        ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.05)`;
        ctx.beginPath();
        ctx.moveTo(beamX, baseY - 2);
        ctx.lineTo(beamTX, hy);
        ctx.lineTo(beamTX, hy + holoH);
        ctx.lineTo(beamX, baseY + 2);
        ctx.closePath();
        ctx.fill();

        // Panel background with scan lines
        ctx.shadowBlur = 6;
        ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.07)`;
        ctx.fillRect(hx, hy, holoW, holoH);
        ctx.shadowBlur = 0;
        ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.10)`;
        for (let sy = hy + 2; sy < hy + holoH - 1; sy += 3) {
            ctx.fillRect(hx + 1, sy, holoW - 2, 1);
        }

        // Content
        ctx.shadowBlur = 4;
        ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.75)`;
        ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.75)`;
        const ctype = Math.floor(Math.abs(Math.sin(seed * 53.1)) * 4);
        if (ctype === 0) {
            // Product silhouette — tall rectangle with label bars
            const pw = holoW * 0.45,
                ph = holoH * 0.42;
            const px = hx + (holoW - pw) / 2,
                py = hy + holoH * 0.1;
            ctx.lineWidth = 0.7;
            ctx.beginPath();
            ctx.rect(px, py, pw, ph);
            ctx.stroke();
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(px, py + ph + 4);
            ctx.lineTo(px + pw, py + ph + 4);
            ctx.moveTo(px + pw * 0.1, py + ph + 7);
            ctx.lineTo(px + pw * 0.9, py + ph + 7);
            ctx.stroke();
        } else if (ctype === 1) {
            // Starburst / empire seal
            const cx = hx + holoW / 2,
                cy = hy + holoH * 0.45;
            const or = Math.min(holoW, holoH) * 0.28,
                ir = or * 0.44;
            ctx.lineWidth = 0.7;
            ctx.beginPath();
            for (let j = 0; j < 8; j++) {
                const ao = (j * Math.PI * 2) / 8 - Math.PI / 2;
                const ai = ao + Math.PI / 8;
                j === 0
                    ? ctx.moveTo(cx + Math.cos(ao) * or, cy + Math.sin(ao) * or)
                    : ctx.lineTo(
                          cx + Math.cos(ao) * or,
                          cy + Math.sin(ao) * or
                      );
                ctx.lineTo(cx + Math.cos(ai) * ir, cy + Math.sin(ai) * ir);
            }
            ctx.closePath();
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(cx, cy, or * 0.28, 0, Math.PI * 2);
            ctx.stroke();
        } else if (ctype === 2) {
            // Humanoid silhouette
            const cx = hx + holoW / 2,
                hr = holoH * 0.11;
            const hdy = hy + holoH * 0.18;
            ctx.lineWidth = 0.7;
            ctx.beginPath();
            ctx.arc(cx, hdy, hr, 0, Math.PI * 2);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(cx - holoW * 0.16, hdy + hr + 1);
            ctx.lineTo(cx - holoW * 0.1, hy + holoH * 0.88);
            ctx.lineTo(cx + holoW * 0.1, hy + holoH * 0.88);
            ctx.lineTo(cx + holoW * 0.16, hdy + hr + 1);
            ctx.closePath();
            ctx.stroke();
        } else {
            // Propaganda text bars (alien script rows)
            const pw = holoW * 0.82,
                px = hx + (holoW - pw) / 2;
            for (let j = 0; j < 3; j++) {
                const ly = hy + holoH * (0.18 + j * 0.26);
                const lw =
                    pw *
                    (0.55 + Math.abs(Math.sin(seed * 19.1 + j * 8.3)) * 0.45);
                ctx.lineWidth = j === 0 ? 1.1 : 0.65;
                ctx.beginPath();
                ctx.moveTo(px, ly);
                ctx.lineTo(px + lw, ly);
                ctx.stroke();
            }
            // Tiny alien glyphs row
            const gw = 3,
                gh = 5,
                gs = 5;
            const gc = Math.floor((pw - 4) / gs);
            ctx.lineWidth = 0.55;
            ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.55)`;
            ctx.fillStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.55)`;
            for (let i = 0; i < gc; i++) {
                const gx = px + i * gs;
                const gi = Math.floor(
                    Math.abs(Math.sin(seed * 61.1 + i * 9.7)) *
                        _ALIEN_GLYPHS.length
                );
                _ALIEN_GLYPHS[gi](ctx, gx, hy + holoH * 0.75, gw, gh);
            }
        }

        // Panel border
        ctx.shadowBlur = 7;
        ctx.strokeStyle = `rgba(${nc[0]},${nc[1]},${nc[2]},0.78)`;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.rect(hx, hy, holoW, holoH);
        ctx.stroke();

        ctx.restore();
    }
};
